// src/health.ts
import { z } from "zod";
var dependencyStatusSchema = z.object({
  name: z.string(),
  status: z.enum(["up", "down"]),
  detail: z.string().optional()
});
var healthStatusSchema = z.object({
  status: z.enum(["ok", "error"]),
  info: z.array(dependencyStatusSchema)
});
var livenessSchema = z.object({
  status: z.literal("ok")
});

// src/version.ts
import { z as z2 } from "zod";
var versionInfoSchema = z2.object({
  service: z2.string(),
  version: z2.string(),
  commit: z2.string(),
  builtAt: z2.string(),
  environment: z2.string()
});

// src/region.ts
import { z as z3 } from "zod";
var DATA_REGIONS = ["us-east-1", "eu-central-1"];
var dataRegionSchema = z3.enum(DATA_REGIONS);
var REGION_PRESENTATION = {
  "us-east-1": { flag: "\u{1F1FA}\u{1F1F8}", short: "US", label: "United States", location: "N. Virginia" },
  "eu-central-1": { flag: "\u{1F1EA}\u{1F1FA}", short: "EU", label: "European Union", location: "Frankfurt" }
};
var EU_COUNTRIES = /* @__PURE__ */ new Set([
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "IS",
  "LI",
  "NO",
  "GB",
  "CH"
]);
function regionForCountry(country) {
  return country && EU_COUNTRIES.has(country.trim().toUpperCase()) ? "eu-central-1" : "us-east-1";
}
function intendedSignupRegion(explicit, country) {
  return explicit ?? regionForCountry(country);
}

// src/auth.ts
import { z as z6 } from "zod";

// src/profile.ts
import { z as z4 } from "zod";
var jobTitleSchema = z4.enum([
  "Software Engineer",
  "Senior Software Engineer",
  "Staff Engineer",
  "Engineering Manager",
  "Director of Engineering",
  "VP of Engineering",
  "CTO",
  "DevOps / SRE",
  "Platform Engineer",
  "Product Manager",
  "Other"
]);
var notificationPreferencesSchema = z4.object({
  memberActivity: z4.boolean(),
  subscriptionAlerts: z4.boolean(),
  domainActivity: z4.boolean(),
  /** Recommendation assignment/updates for items you own (ACT-011). */
  recommendations: z4.boolean(),
  /** The Monday weekly Optimization Digest, scoped to your domains (Feature 26). */
  weeklyDigest: z4.boolean()
});
var DEFAULT_NOTIFICATION_PREFERENCES = {
  memberActivity: true,
  subscriptionAlerts: true,
  domainActivity: true,
  recommendations: true,
  weeklyDigest: true
};
function normalizeNotificationPreferences(raw) {
  const parsed = notificationPreferencesSchema.partial().safeParse(raw);
  return { ...DEFAULT_NOTIFICATION_PREFERENCES, ...parsed.success ? parsed.data : {} };
}
var COMMON_TIMEZONES = [
  "UTC",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Vienna",
  "Europe/Paris",
  "Europe/Madrid",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney"
];

// src/organization.ts
import { z as z5 } from "zod";
var companySizeSchema = z5.enum(["1-10", "11-50", "51-200", "201-1000", "1000+"]);
var industrySchema = z5.enum([
  "Software & Technology",
  "Financial Services",
  "Healthcare & Life Sciences",
  "E-commerce & Retail",
  "Manufacturing",
  "Media & Entertainment",
  "Education",
  "Telecommunications",
  "Energy & Utilities",
  "Government & Public Sector",
  "Transportation & Logistics",
  "Real Estate & Construction",
  "Professional Services",
  "Travel & Hospitality",
  "Nonprofit",
  "Other"
]);
var organizationStatusSchema = z5.enum(["ONBOARDING", "ACTIVE", "DELETED"]);
var orgKindSchema = z5.enum(["CUSTOMER", "SANDBOX", "INTERNAL"]);
var ONBOARDING_STEPS = [
  "domain",
  "connect",
  "billing",
  "invite",
  "sync",
  "celebrate",
  "done"
];
var onboardingStepSchema = z5.enum(ONBOARDING_STEPS);
var onboardingStateSchema = z5.object({
  status: organizationStatusSchema,
  onboardingStep: onboardingStepSchema
});
var advanceOnboardingSchema = z5.object({ from: onboardingStepSchema });
var mfaPolicySchema = z5.enum(["OPTIONAL", "REQUIRED", "REQUIRED_PHISHING_RESISTANT"]);
var organizationSchema = z5.object({
  id: z5.string().uuid(),
  name: z5.string(),
  slug: z5.string(),
  industry: z5.string().nullable(),
  companySize: z5.string().nullable(),
  websiteUrl: z5.string().nullable(),
  billingEmail: z5.string().nullable(),
  billingCompanyName: z5.string().nullable(),
  status: organizationStatusSchema,
  onboardingStep: onboardingStepSchema,
  mfaPolicy: mfaPolicySchema,
  // Opt-in privacy controls (data minimization). Default true = full functionality.
  ingestSourceEnabled: z5.boolean(),
  aiCorrelationEnabled: z5.boolean(),
  // Terminal, one-way name-tokenization lever. Default false = real names stored;
  // once true it cannot be turned back off (the plaintext is irreversibly purged).
  tokenizeResourceNames: z5.boolean(),
  /** Data region the org is hosted in (docs/tech/07). Immutable after signup. */
  region: dataRegionSchema,
  createdAt: z5.string()
});
var updateOrganizationSchema = z5.object({
  name: z5.string().min(1).max(120).optional(),
  industry: industrySchema.nullable().optional(),
  companySize: companySizeSchema.nullable().optional(),
  websiteUrl: z5.string().url().max(2048).nullable().optional(),
  billingEmail: z5.string().email().max(255).nullable().optional(),
  billingCompanyName: z5.string().min(1).max(200).nullable().optional()
});
var updateMfaPolicySchema = z5.object({ mfaPolicy: mfaPolicySchema });
var updatePrivacySettingsSchema = z5.object({
  ingestSourceEnabled: z5.boolean().optional(),
  aiCorrelationEnabled: z5.boolean().optional()
});
var privacyPurgeResultSchema = z5.object({
  sourceSnippetsStripped: z5.number(),
  explanationsDeleted: z5.number(),
  capabilityContextsDeleted: z5.number(),
  recommendationsCleared: z5.number(),
  aiEdgesRemoved: z5.number()
});
var updatePrivacySettingsResultSchema = z5.object({
  organization: organizationSchema,
  purged: privacyPurgeResultSchema.nullable()
});
var enableNameTokenizationSchema = z5.object({
  confirm: z5.string().min(1)
});
var nameTokenizationResultSchema = z5.object({
  nodesRehashed: z5.number(),
  recommendationsRehashed: z5.number(),
  explanationsDeleted: z5.number(),
  capabilityContextsDeleted: z5.number()
});
var enableNameTokenizationResultSchema = z5.object({
  organization: organizationSchema,
  tokenized: nameTokenizationResultSchema
});
var deleteAccountSchema = z5.object({ confirmName: z5.string().min(1).max(120) });

// src/auth.ts
var orgRoleSchema = z6.enum(["ADMINISTRATOR", "MANAGER", "ENGINEER", "VIEWER"]);
var PASSWORD_MIN_LENGTH = 12;
var PASSWORD_MAX_LENGTH = 128;
var passwordSchema = z6.string().min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`).max(PASSWORD_MAX_LENGTH, `Password must be at most ${PASSWORD_MAX_LENGTH} characters`).regex(/[a-z]/, "Password must contain a lowercase letter").regex(/[A-Z]/, "Password must contain an uppercase letter").regex(/[0-9]/, "Password must contain a number").regex(/[^A-Za-z0-9]/, "Password must contain a special character");
var PASSWORD_RULES = [
  `At least ${PASSWORD_MIN_LENGTH} characters`,
  "An uppercase and a lowercase letter",
  "A number",
  "A special character"
];
var registerSchema = z6.object({
  email: z6.string().email(),
  password: passwordSchema,
  name: z6.string().min(1),
  organizationName: z6.string().min(1),
  /** Private-beta signup token from the emailed invite link (Feature 27).
   * Required by the server only when the invite-only gate is on. */
  invite: z6.string().min(1).max(200).optional(),
  /** The data region the user chose for this org (data residency, SEC H5). The
   * server enforces it matches the stack serving the request; on mismatch it
   * returns 409 REGION_MISMATCH with the host to retry on. Omitted → inferred
   * from the viewer's geo. */
  region: dataRegionSchema.optional()
});
var registrationInfoSchema = z6.object({
  betaInviteRequired: z6.boolean(),
  /** The data region a signup on THIS stack lands in (stamped onto the new org). */
  region: dataRegionSchema,
  /** Region pre-selected from the request's viewer country; null when no geo signal.
   * Client preference order: saved cookie → this → `region`. */
  geoRegion: dataRegionSchema.nullable(),
  /** App origin for each region, so the client can redirect to the correct host. */
  regionHosts: z6.record(dataRegionSchema, z6.string().url())
});
var loginSchema = z6.object({
  email: z6.string().email(),
  password: z6.string().min(1)
});
var userProfileSchema = z6.object({
  id: z6.string().uuid(),
  email: z6.string().email(),
  name: z6.string().nullable(),
  organizationId: z6.string().uuid(),
  organizationName: z6.string(),
  organizationStatus: organizationStatusSchema,
  onboardingStep: onboardingStepSchema,
  /** Whether this is a real customer org or a self-serve sandbox demo. */
  organizationKind: orgKindSchema,
  /** When the sandbox expires (ISO), or null for real customers. Drives the
   * "you're in a demo" banner + countdown. */
  sandboxExpiresAt: z6.string().nullable(),
  role: orgRoleSchema,
  emailVerified: z6.boolean(),
  /** Whether the user has at least one active (verified) MFA factor. Drives the
   * Security card status + the "add extra security" nudge (Feature 25). */
  mfaEnabled: z6.boolean(),
  /** Whether the org's policy requires MFA for this user (a password user under a
   * REQUIRED policy). With mfaEnabled=false this forces mandatory enrollment
   * (Feature 25, Phase B). SSO/passwordless users are never enforced. */
  mfaEnforced: z6.boolean(),
  // Profile detail (read-lenient for legacy values); notification prefs are
  // always present (server fills defaults).
  jobTitle: z6.string().nullable(),
  timezone: z6.string().nullable(),
  avatarUrl: z6.string().nullable(),
  notificationPreferences: notificationPreferencesSchema,
  /** Deployment capability: the periodic validation scheduler is OFF here, so an
   * admin can trigger a validation run on demand (staging/local). False in prod,
   * where validation is continuous and there's nothing to trigger. */
  manualValidationRun: z6.boolean()
});
var verifyEmailSchema = z6.object({ token: z6.string().min(1) });
var forgotPasswordSchema = z6.object({ email: z6.string().email() });
var resetPasswordSchema = z6.object({
  token: z6.string().min(1),
  password: passwordSchema
});
var authResponseSchema = z6.object({
  user: userProfileSchema,
  accessToken: z6.string()
});
var mfaFactorTypeSchema = z6.enum(["TOTP", "WEBAUTHN"]);
var mfaFactorSchema = z6.object({
  id: z6.string().uuid(),
  type: mfaFactorTypeSchema,
  label: z6.string(),
  createdAt: z6.string(),
  lastUsedAt: z6.string().nullable()
});
var mfaStatusSchema = z6.object({
  enabled: z6.boolean(),
  factors: z6.array(mfaFactorSchema),
  recoveryCodesRemaining: z6.number().int().nonnegative()
});
var mfaEnrollStartSchema = z6.object({
  factorId: z6.string().uuid(),
  /** otpauth:// URI encoded into the QR. */
  otpauthUri: z6.string(),
  /** Base32 secret for manual entry when a QR can't be scanned. */
  secret: z6.string(),
  /** Pre-rendered QR as a data: URL (schema-first; no client QR lib needed). */
  qrDataUrl: z6.string()
});
var mfaActivateSchema = z6.object({
  factorId: z6.string().uuid(),
  code: z6.string().min(1)
});
var mfaRecoveryCodesSchema = z6.object({
  recoveryCodes: z6.array(z6.string())
});
var mfaVerifyLoginSchema = z6.object({
  challengeToken: z6.string().min(1),
  code: z6.string().min(1)
});
var mfaChallengeSchema = z6.object({
  mfaRequired: z6.literal(true),
  challengeToken: z6.string()
});
var loginResultSchema = z6.union([mfaChallengeSchema, authResponseSchema]);
var webauthnOptionsSchema = z6.record(z6.unknown());
var webauthnRegisterVerifySchema = z6.object({
  label: z6.string().min(1).max(60).optional(),
  response: z6.record(z6.unknown())
});
var webauthnLoginOptionsSchema = z6.object({ challengeToken: z6.string().min(1) });
var webauthnLoginVerifySchema = z6.object({
  challengeToken: z6.string().min(1),
  response: z6.record(z6.unknown())
});

// src/rbac.ts
var Permission = /* @__PURE__ */ ((Permission2) => {
  Permission2["OrgManage"] = "org:manage";
  Permission2["OrgRead"] = "org:read";
  Permission2["UsersManage"] = "users:manage";
  Permission2["UsersRead"] = "users:read";
  Permission2["DomainsManage"] = "domains:manage";
  Permission2["DomainsRead"] = "domains:read";
  Permission2["IntegrationsManage"] = "integrations:manage";
  Permission2["IntegrationsRead"] = "integrations:read";
  Permission2["RecommendationsManage"] = "recommendations:manage";
  Permission2["RecommendationsRead"] = "recommendations:read";
  Permission2["ReviewsManage"] = "reviews:manage";
  Permission2["ReviewsRead"] = "reviews:read";
  return Permission2;
})(Permission || {});
var ALL = Object.values(Permission);
var ROLE_PERMISSIONS = {
  ADMINISTRATOR: ALL,
  MANAGER: [
    "org:read" /* OrgRead */,
    "users:manage" /* UsersManage */,
    "users:read" /* UsersRead */,
    "domains:manage" /* DomainsManage */,
    "domains:read" /* DomainsRead */,
    "integrations:manage" /* IntegrationsManage */,
    "integrations:read" /* IntegrationsRead */,
    "recommendations:manage" /* RecommendationsManage */,
    "recommendations:read" /* RecommendationsRead */,
    // Admins & managers configure CI/CD Reviews (REV / req 4).
    "reviews:manage" /* ReviewsManage */,
    "reviews:read" /* ReviewsRead */
  ],
  ENGINEER: [
    "org:read" /* OrgRead */,
    "users:read" /* UsersRead */,
    "domains:read" /* DomainsRead */,
    // Engineers manage integrations for domains they can access (RBAC-007).
    "integrations:manage" /* IntegrationsManage */,
    "integrations:read" /* IntegrationsRead */,
    "recommendations:manage" /* RecommendationsManage */,
    "recommendations:read" /* RecommendationsRead */,
    // Engineers can read Reviews but not toggle them.
    "reviews:read" /* ReviewsRead */
  ],
  VIEWER: [
    "org:read" /* OrgRead */,
    "users:read" /* UsersRead */,
    "domains:read" /* DomainsRead */,
    "integrations:read" /* IntegrationsRead */,
    "recommendations:read" /* RecommendationsRead */,
    "reviews:read" /* ReviewsRead */
  ]
};
function permissionsForRole(role) {
  return ROLE_PERMISSIONS[role];
}
function roleHasPermissions(role, required) {
  const granted = new Set(ROLE_PERMISSIONS[role]);
  return required.every((p) => granted.has(p));
}
function canAssignRole(actor, target) {
  if (actor === "ADMINISTRATOR") return true;
  if (actor === "MANAGER") return target === "ENGINEER" || target === "VIEWER";
  return false;
}

// src/domain.ts
import { z as z8 } from "zod";

// src/integration.ts
import { z as z7 } from "zod";
var integrationProviderSchema = z7.enum([
  "GITHUB",
  "GITLAB",
  "AWS",
  "AZURE",
  "GCP",
  "SANDBOX"
]);
var integrationKindSchema = z7.enum(["SCM", "CLOUD"]);
var integrationStatusSchema = z7.enum([
  "PENDING",
  "CONNECTED",
  "SYNCING",
  "WARNING",
  "ERROR",
  "DISCONNECTED"
]);
var syncRunStatusSchema = z7.enum(["QUEUED", "RUNNING", "SUCCEEDED", "FAILED"]);
var syncTriggerSchema = z7.enum(["SCHEDULED", "MANUAL"]);
var integrationBillingStatusSchema = z7.enum([
  "ok",
  "pending",
  "error",
  "not_configured",
  "n/a"
]);
var integrationSchema = z7.object({
  id: z7.string().uuid(),
  domainId: z7.string().uuid(),
  provider: integrationProviderSchema,
  kind: integrationKindSchema,
  status: integrationStatusSchema,
  displayName: z7.string(),
  /** Non-secret connection configuration (e.g. repo owner, AWS role ARN, region). */
  config: z7.record(z7.unknown()).nullable(),
  lastSyncedAt: z7.string().nullable(),
  lastError: z7.string().nullable(),
  /** Billed-cost read health (distinct from sync health — a source can sync fine but
   * still fail to yield billed cost, e.g. GCP without a billing export). Optional for
   * deploy-window compatibility; the API always sets it (defaults to 'n/a'). */
  billingStatus: integrationBillingStatusSchema.optional(),
  createdAt: z7.string(),
  updatedAt: z7.string()
});
var syncRunSchema = z7.object({
  id: z7.string().uuid(),
  integrationId: z7.string().uuid(),
  status: syncRunStatusSchema,
  trigger: syncTriggerSchema,
  stage: z7.string().nullable(),
  progressPct: z7.number().int(),
  startedAt: z7.string().nullable(),
  finishedAt: z7.string().nullable(),
  error: z7.string().nullable(),
  stats: z7.record(z7.unknown()).nullable(),
  createdAt: z7.string()
});
var integrationErrorCodeSchema = z7.enum(["auth", "connectivity", "unknown"]);
var regionScanSchema = z7.object({
  scanned: z7.array(z7.string()),
  total: z7.number().int(),
  truncated: z7.boolean()
});
var integrationHealthSchema = z7.object({
  integrationId: z7.string().uuid(),
  status: integrationStatusSchema,
  lastSyncedAt: z7.string().nullable(),
  lastError: z7.string().nullable(),
  /** Classification of the most recent failure, if any. */
  lastErrorCode: integrationErrorCodeSchema.nullable(),
  /** True when the integration requires user intervention (auth failure or a
   * hard error with no prior successful sync). */
  needsAttention: z7.boolean(),
  /** When the next manual sync is allowed (ISO), or null if one can be triggered
   * now. Manual syncs are rate-limited to once every 15 minutes. */
  manualSyncAvailableAt: z7.string().nullable(),
  /** Regions covered by the latest successful run (INT / region visibility), or
   * null when the provider reports no region scope or has never synced. */
  regions: regionScanSchema.nullable(),
  recentRuns: z7.array(syncRunSchema)
});
var connectIntegrationSchema = z7.object({
  provider: integrationProviderSchema,
  displayName: z7.string().min(1).max(120),
  config: z7.record(z7.unknown()).default({}),
  credentials: z7.record(z7.unknown()).default({})
});
var githubConfigSchema = z7.object({
  installationId: z7.string().min(1),
  repository: z7.string().optional()
});
var githubConnectRequestSchema = z7.object({
  repository: z7.string().min(1),
  displayName: z7.string().max(120).optional()
});
var githubConnectResultSchema = z7.discriminatedUnion("status", [
  z7.object({ status: z7.literal("connected"), integration: integrationSchema }),
  z7.object({ status: z7.literal("install_required"), installUrl: z7.string().url() }),
  z7.object({ status: z7.literal("unavailable"), reason: z7.string() })
]);
var gitlabConfigSchema = z7.object({
  baseUrl: z7.string().url(),
  groupPath: z7.string().optional()
});
var gitlabConnectRequestSchema = z7.object({
  token: z7.string().min(1),
  baseUrl: z7.string().url().default("https://gitlab.com"),
  groupPath: z7.string().max(200).optional(),
  displayName: z7.string().max(120).optional()
});
var gitlabConnectResultSchema = z7.discriminatedUnion("status", [
  z7.object({ status: z7.literal("connected"), integration: integrationSchema }),
  z7.object({ status: z7.literal("error"), reason: z7.string() })
]);
var kubernetesAccessSchema = z7.object({
  enabled: z7.boolean(),
  autoProvision: z7.boolean().optional()
});
var awsConfigSchema = z7.object({
  roleArn: z7.string().min(1),
  regions: z7.array(z7.string()).optional(),
  kubernetesAccess: kubernetesAccessSchema.optional()
});
var setAwsKubernetesAccessSchema = z7.object({
  kubernetesAccess: kubernetesAccessSchema.nullable()
});
var awsKubernetesAccessTestResultSchema = z7.object({
  ok: z7.boolean(),
  clusterCount: z7.number().optional(),
  workloadCount: z7.number().optional(),
  unauthorized: z7.boolean().optional(),
  message: z7.string().optional(),
  setupUrl: z7.string().optional()
});
var awsStateBackendSchema = z7.object({
  bucket: z7.string().trim().min(1),
  key: z7.string().trim().min(1),
  kmsKeyArn: z7.string().trim().min(1).optional()
});
var setAwsStateBackendSchema = z7.object({
  stateBackend: awsStateBackendSchema.nullable()
});
var testAwsStateBackendSchema = z7.object({ stateBackend: awsStateBackendSchema });
var awsStateBackendTestResultSchema = z7.object({
  ok: z7.boolean(),
  resourceCount: z7.number().optional(),
  message: z7.string().optional(),
  setupUrl: z7.string().optional()
});
var awsPrepareResultSchema = z7.object({
  externalId: z7.string(),
  cloudFormationUrl: z7.string().url(),
  lizrdAccountId: z7.string()
});
var awsConnectRequestSchema = z7.object({
  roleArn: z7.string().min(1),
  externalId: z7.string().min(1),
  regions: z7.array(z7.string()).optional(),
  /** Optional S3 Terraform-state backend, set during connect (TD-058 Phase B). */
  stateBackend: awsStateBackendSchema.optional(),
  displayName: z7.string().max(120).optional()
});
var awsTestStateBackendRequestSchema = z7.object({
  roleArn: z7.string().min(1),
  externalId: z7.string().min(1),
  regions: z7.array(z7.string()).optional(),
  stateBackend: awsStateBackendSchema
});
var gcpStateBackendSchema = z7.object({
  bucket: z7.string().min(1),
  object: z7.string().min(1)
});
var gcpConfigSchema = z7.object({
  projectId: z7.string().min(1),
  serviceAccountEmail: z7.string().min(1),
  /** Full WIF provider resource name:
   * `projects/<num>/locations/global/workloadIdentityPools/<pool>/providers/<provider>`. */
  workloadIdentityProvider: z7.string().min(1),
  regions: z7.array(z7.string()).optional(),
  /** Optional BigQuery billing-export table (`project.dataset.table`) — the CUR
   * analogue. When set, per-service billed cost reconciles the list-price estimates
   * (Stage 4 G3); when absent, costs stay list-price estimates (graceful degrade). */
  billingExport: z7.object({ table: z7.string().min(1) }).optional(),
  /** Optional GCS Terraform-state backend (bucket + object path to `terraform.tfstate`).
   * When set, state ingestion stamps confidence-1.0 code↔cloud correlation (Stage 4 G5). */
  stateBackend: gcpStateBackendSchema.optional(),
  /** Opt-in Kubernetes (GKE) pod-level discovery (K3). The customer grants the viewer
   * SA a read-only ClusterRole (via a ClusterRoleBinding on its email) + reachable
   * endpoint; no secret is stored — the cluster is read with the impersonated-SA token. */
  kubernetesAccess: kubernetesAccessSchema.optional()
});
var azureStateBackendSchema = z7.object({
  storageAccount: z7.string().min(1),
  container: z7.string().min(1),
  key: z7.string().min(1)
});
var azureConfigSchema = z7.object({
  tenantId: z7.string().min(1),
  subscriptionId: z7.string().min(1),
  regions: z7.array(z7.string()).optional(),
  /** Opt-in AKS pod-level discovery (A5), the EKS/GKE twin — read-only, keyless. */
  kubernetesAccess: kubernetesAccessSchema.optional(),
  /** Opt-in azurerm Terraform state ingestion (A6) — code↔cloud provenance. */
  stateBackend: azureStateBackendSchema.optional()
});
var stateBackendSchema = z7.discriminatedUnion("provider", [
  awsStateBackendSchema.extend({ provider: z7.literal("AWS") }),
  gcpStateBackendSchema.extend({ provider: z7.literal("GCP") }),
  azureStateBackendSchema.extend({ provider: z7.literal("AZURE") })
]);
var setStateBackendSchema = z7.object({ stateBackend: stateBackendSchema.nullable() });
var testStateBackendSchema = z7.object({ stateBackend: stateBackendSchema });
var stateBackendTestResultSchema = awsStateBackendTestResultSchema;
var azurePrepareResultSchema = z7.object({
  appClientId: z7.string(),
  setupScript: z7.string()
});
var azureConnectRequestSchema = z7.object({
  tenantId: z7.string().min(1),
  subscriptionId: z7.string().min(1),
  displayName: z7.string().max(120).optional(),
  regions: z7.array(z7.string()).optional(),
  /** Optional azurerm blob Terraform-state backend, set during connect (A6). */
  stateBackend: azureStateBackendSchema.optional()
});
var azureTestStateBackendRequestSchema = z7.object({
  tenantId: z7.string().min(1),
  subscriptionId: z7.string().min(1),
  stateBackend: azureStateBackendSchema
});
var gcpPrepareResultSchema = z7.object({
  externalId: z7.string(),
  lizrdAwsPrincipal: z7.string(),
  setupScript: z7.string()
});
var gcpConnectRequestSchema = z7.object({
  projectId: z7.string().min(1),
  serviceAccountEmail: z7.string().min(1),
  workloadIdentityProvider: z7.string().min(1),
  /** The setup id from `prepare` — bookkeeping that correlates connect to setup. */
  externalId: z7.string().min(1),
  regions: z7.array(z7.string()).optional(),
  /** Optional BigQuery billing-export table (`project.dataset.table`) for billed-cost
   * reconciliation (Stage 4 G3). */
  billingExportTable: z7.string().optional(),
  /** Optional GCS Terraform-state backend for state ingestion (Stage 4 G5). */
  stateBackend: gcpStateBackendSchema.optional(),
  displayName: z7.string().max(120).optional()
});
var gcpTestStateBackendRequestSchema = z7.object({
  projectId: z7.string().min(1),
  serviceAccountEmail: z7.string().min(1),
  workloadIdentityProvider: z7.string().min(1),
  stateBackend: gcpStateBackendSchema
});
var integrationValidationResultSchema = z7.object({
  ok: z7.boolean(),
  message: z7.string().optional(),
  code: integrationErrorCodeSchema.optional()
});
var domainSyncResultSchema = z7.object({
  triggered: z7.number().int(),
  skipped: z7.number().int(),
  lastSyncedAt: z7.string().nullable()
});
var integrationSyncStatusSchema = z7.object({
  integrationId: z7.string(),
  displayName: z7.string(),
  provider: integrationProviderSchema,
  status: z7.string(),
  runStatus: z7.string().nullable(),
  stage: z7.string().nullable(),
  progressPct: z7.number().int(),
  /** Regions covered by the latest run, for live pipeline visibility. Null until
   * a run with a region scope has completed. */
  regions: regionScanSchema.nullable()
});
var graphSyncStageSchema = z7.enum(["IDLE", "PENDING", "RUNNING", "DONE", "FAILED"]);
var domainSyncStatusSchema = z7.object({
  integrations: z7.array(integrationSyncStatusSchema),
  graph: z7.object({
    stage: graphSyncStageSchema,
    at: z7.string().nullable()
  })
});
var integrationSyncJobSchema = z7.object({
  runId: z7.string().uuid(),
  integrationId: z7.string().uuid(),
  domainId: z7.string().uuid(),
  organizationId: z7.string().uuid(),
  trigger: syncTriggerSchema
});
var INTEGRATION_SYNC_QUEUE = "integration-sync";
var graphSyncJobSchema = z7.object({
  organizationId: z7.string().uuid(),
  domainId: z7.string().uuid()
});
var GRAPH_SYNC_QUEUE = "graph-sync";
var normalizedEntitySchema = z7.object({
  /** Provider-specific external id, stable across syncs (provenance). */
  externalId: z7.string(),
  /** Canonical entity type, e.g. 'repository', 'compute.instance', 'cost.record'. */
  type: z7.string(),
  provider: integrationProviderSchema,
  name: z7.string(),
  metadata: z7.record(z7.unknown()).default({}),
  /** Edges to other entities by externalId (tech design §Relationship Model). */
  relationships: z7.array(
    z7.object({
      type: z7.string(),
      targetExternalId: z7.string()
    })
  ).default([])
});

// src/domain.ts
var domainStatusSchema = z8.enum(["ACTIVE", "SUSPENDED", "ARCHIVED"]);
var domainSchema = z8.object({
  id: z8.string().uuid(),
  organizationId: z8.string().uuid(),
  name: z8.string(),
  slug: z8.string(),
  status: domainStatusSchema,
  /** System setting: show the realized-savings leaderboard on Cost Optimization. */
  showLeaderboard: z8.boolean(),
  /** Whether CI/CD pull-request Reviews are enabled for this domain. Drives the
   * conditional "Reviews" nav item; the full review config is managed via the
   * dedicated reviews-settings endpoint, not here. */
  reviewsEnabled: z8.boolean(),
  /** Whether the NIS2 compliance lens is enabled for this domain. Drives the
   * conditional "Compliance" nav item and the dashboard posture widgets; the full
   * policy is managed via the compliance-settings endpoint. */
  complianceEnabled: z8.boolean(),
  createdAt: z8.string(),
  updatedAt: z8.string()
});
var createDomainSchema = z8.object({
  name: z8.string().min(1).max(80)
});
var updateDomainSchema = z8.object({
  name: z8.string().min(1).max(80).optional(),
  showLeaderboard: z8.boolean().optional()
}).refine((v) => v.name !== void 0 || v.showLeaderboard !== void 0, {
  message: "Provide at least one field to update."
});
var setDomainStatusSchema = z8.object({
  status: domainStatusSchema
});
var grantDomainAccessSchema = z8.object({
  userId: z8.string().uuid()
});
var integrationHealthRowSchema = z8.object({
  id: z8.string().uuid(),
  provider: integrationProviderSchema,
  kind: integrationKindSchema,
  displayName: z8.string(),
  status: integrationStatusSchema,
  lastSyncedAt: z8.string().nullable(),
  lastError: z8.string().nullable()
});
var domainHealthStatusSchema = z8.enum([
  "healthy",
  "attention",
  "error",
  "syncing",
  "empty"
]);
var domainHealthSchema = z8.object({
  domainId: z8.string().uuid(),
  name: z8.string(),
  /** Domain lifecycle (ACTIVE/SUSPENDED/ARCHIVED), distinct from sync health. */
  status: domainStatusSchema,
  /** Worst status across the domain's integrations. */
  overall: domainHealthStatusSchema,
  counts: z8.object({
    total: z8.number().int(),
    healthy: z8.number().int(),
    attention: z8.number().int(),
    error: z8.number().int(),
    syncing: z8.number().int()
  }),
  /** Most recent successful sync across all integrations, or null if none yet. */
  lastSyncedAt: z8.string().nullable(),
  integrations: z8.array(integrationHealthRowSchema)
});

// src/member.ts
import { z as z9 } from "zod";
var memberSchema = z9.object({
  id: z9.string().uuid(),
  email: z9.string().email(),
  name: z9.string().nullable(),
  role: orgRoleSchema,
  emailVerified: z9.boolean(),
  /** Whether the member has an active MFA factor (Feature 25 — admin visibility). */
  mfaEnabled: z9.boolean(),
  domainIds: z9.array(z9.string().uuid()),
  /** When the member joined the organization. */
  createdAt: z9.string(),
  /** Last platform access (login/refresh); null until first login. */
  lastLoginAt: z9.string().nullable()
});
var updateMemberRoleSchema = z9.object({
  role: orgRoleSchema
});
var setMemberDomainsSchema = z9.object({
  domainIds: z9.array(z9.string().uuid())
});
var updateProfileSchema = z9.object({
  name: z9.string().min(1).max(120).optional(),
  jobTitle: jobTitleSchema.nullable().optional(),
  timezone: z9.string().max(64).nullable().optional(),
  avatarUrl: z9.string().url().max(2048).nullable().optional(),
  notificationPreferences: notificationPreferencesSchema.partial().optional()
});

// src/invitation.ts
import { z as z10 } from "zod";
var invitationStatusSchema = z10.enum(["PENDING", "ACCEPTED", "EXPIRED", "REVOKED"]);
var invitationPreviewSchema = z10.object({
  email: z10.string().email(),
  organizationName: z10.string(),
  role: orgRoleSchema
});
var invitationSchema = z10.object({
  id: z10.string().uuid(),
  email: z10.string().email(),
  role: orgRoleSchema,
  domainIds: z10.array(z10.string().uuid()),
  status: invitationStatusSchema,
  expiresAt: z10.string(),
  createdAt: z10.string()
});
var createInvitationSchema = z10.object({
  email: z10.string().email(),
  role: orgRoleSchema.default("VIEWER"),
  domainIds: z10.array(z10.string().uuid()).default([])
});
var acceptInvitationSchema = z10.object({
  token: z10.string().min(1),
  name: z10.string().min(1).max(120),
  password: passwordSchema
});

// src/subscription.ts
import { z as z11 } from "zod";
var subscriptionPlanSchema = z11.enum(["STARTER", "PROFESSIONAL", "BUSINESS", "ENTERPRISE"]);
var usageMeterSchema = z11.object({
  used: z11.number().int().nonnegative(),
  max: z11.number().int().nonnegative()
});
var subscriptionUsageSchema = z11.object({
  plan: subscriptionPlanSchema,
  domains: usageMeterSchema,
  users: usageMeterSchema
});

// src/billing.ts
import { z as z12 } from "zod";
var billingCostStatusSchema = z12.enum([
  "OK",
  "PENDING",
  "STALE",
  "ERROR_PERMISSIONS",
  "EXPORT_NOT_CONFIGURED",
  "ERROR"
]);
var accountSpendSchema = z12.object({
  integrationId: z12.string().uuid(),
  domainId: z12.string().uuid(),
  provider: z12.string(),
  amount: z12.number(),
  currency: z12.string(),
  status: billingCostStatusSchema,
  finalized: z12.boolean()
});
var projectionBasisSchema = z12.enum(["run-rate", "prior-month", "actual"]);
var spendUnderManagementSchema = z12.object({
  month: z12.string().regex(/^\d{4}-\d{2}$/),
  total: z12.number(),
  projectedTotal: z12.number(),
  projectionBasis: projectionBasisSchema,
  currency: z12.string(),
  complete: z12.boolean(),
  finalized: z12.boolean(),
  accounts: z12.array(accountSpendSchema)
});
var round2 = (n) => Math.round(n * 100) / 100;
function monthProgress(now = /* @__PURE__ */ new Date()) {
  const daysInMonth = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)
  ).getUTCDate();
  const daysElapsed = Math.max(1, now.getUTCDate() - 1);
  return { daysElapsed, daysInMonth };
}
function projectMonthlySpend(monthToDate, opts) {
  const { daysElapsed, daysInMonth, priorMonth } = opts;
  if (daysElapsed >= daysInMonth) return { projected: round2(monthToDate), basis: "actual" };
  if (daysElapsed <= 3 && priorMonth != null && priorMonth > 0) {
    return { projected: round2(priorMonth), basis: "prior-month" };
  }
  return { projected: round2(monthToDate * daysInMonth / daysElapsed), basis: "run-rate" };
}
var PRICING_BANDS = [
  { upTo: 5e3, fee: 99, label: "Up to $5k" },
  { upTo: 15e3, fee: 249, label: "$5k\u2013$15k" },
  { upTo: 4e4, fee: 599, label: "$15k\u2013$40k" },
  { upTo: 1e5, fee: 1299, label: "$40k\u2013$100k" },
  { upTo: Infinity, fee: null, label: "$100k+" }
];
var bandSchema = z12.object({
  label: z12.string(),
  monthlyFee: z12.number().nullable(),
  isCustom: z12.boolean()
});
function bandForSpend(totalUsd) {
  const band = PRICING_BANDS.find((b) => totalUsd <= b.upTo);
  const fee = band ? band.fee : null;
  const label = band ? band.label : "$100k+";
  return { label, monthlyFee: fee, isCustom: fee === null };
}
function pricingBands() {
  return PRICING_BANDS.map((b) => ({
    label: b.label,
    monthlyFee: b.fee,
    isCustom: b.fee === null
  }));
}
var TRIAL_DAYS = 7;
var DAY_MS = 864e5;
function monthlyCharge(input) {
  const parts = input.month.split("-");
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const monthStart = Math.floor(Date.UTC(y, m - 1, 1) / DAY_MS);
  const monthEnd = Math.floor(Date.UTC(y, m, 1) / DAY_MS);
  const daysInMonth = monthEnd - monthStart;
  const signupDay = Math.floor(input.signupAt.getTime() / DAY_MS);
  const trialEndDay = signupDay + TRIAL_DAYS;
  const activeDays = Math.max(0, monthEnd - Math.max(monthStart, signupDay));
  const freeDays = Math.max(0, Math.min(monthEnd, trialEndDay) - Math.max(monthStart, signupDay));
  const billableDays = Math.max(0, activeDays - freeDays);
  if (input.monthlyFee === null) {
    return {
      daysInMonth,
      activeDays,
      freeDays,
      billableDays,
      fee: null,
      credit: 0,
      amountDue: null
    };
  }
  const amountDue = round2(input.monthlyFee * billableDays / daysInMonth);
  const credit = round2(input.monthlyFee - amountDue);
  return {
    daysInMonth,
    activeDays,
    freeDays,
    billableDays,
    fee: input.monthlyFee,
    credit,
    amountDue
  };
}
var statementStatusSchema = z12.enum(["issued", "paid", "void"]);
var billingStatementSchema = z12.object({
  month: z12.string().regex(/^\d{4}-\d{2}$/),
  spend: z12.number(),
  currency: z12.string(),
  bandLabel: z12.string(),
  fee: z12.number().nullable(),
  credit: z12.number(),
  amountDue: z12.number().nullable(),
  isCustom: z12.boolean(),
  status: statementStatusSchema
});
var BILLING_GRACE_DAYS = 5;
var billingAccessStateSchema = z12.object({
  ok: z12.boolean(),
  lostAt: z12.string().nullable(),
  graceEndsAt: z12.string().nullable(),
  daysRemaining: z12.number(),
  restricted: z12.boolean(),
  /** The specific cloud accounts whose billed-cost read is broken, so the banner can
   * name them and link to the right domain's integrations page. Populated by the API
   * summary (not the pure state fn); empty/absent when access is fine. */
  affected: z12.array(
    z12.object({
      integrationId: z12.string().uuid(),
      domainId: z12.string().uuid(),
      domainName: z12.string(),
      provider: z12.string(),
      displayName: z12.string()
    })
  ).optional()
});
function billingAccessStateFrom(lostAt, now = /* @__PURE__ */ new Date()) {
  if (!lostAt) {
    return {
      ok: true,
      lostAt: null,
      graceEndsAt: null,
      daysRemaining: BILLING_GRACE_DAYS,
      restricted: false
    };
  }
  const lost = typeof lostAt === "string" ? new Date(lostAt) : lostAt;
  const graceEnds = new Date(lost.getTime() + BILLING_GRACE_DAYS * 864e5);
  const daysRemaining = Math.max(0, Math.ceil((graceEnds.getTime() - now.getTime()) / 864e5));
  return {
    ok: false,
    lostAt: lost.toISOString(),
    graceEndsAt: graceEnds.toISOString(),
    daysRemaining,
    restricted: now.getTime() > graceEnds.getTime()
  };
}
var BILLING_ACCESS_ARM_DEBOUNCE_MS = 6 * 36e5;
var BILLING_ACCESS_CLEAR_DEBOUNCE_MS = 24 * 36e5;
function billingHealthTransition(state, lost, now = /* @__PURE__ */ new Date(), cfg = {}) {
  const armMs = cfg.armMs ?? BILLING_ACCESS_ARM_DEBOUNCE_MS;
  const clearMs = cfg.clearMs ?? BILLING_ACCESS_CLEAR_DEBOUNCE_MS;
  let { lostAt, unhealthySince, healthySince } = state;
  let clearedNotifiedAt = false;
  let armed = false;
  if (lost) {
    healthySince = null;
    if (unhealthySince === null) unhealthySince = now;
    if (lostAt === null && now.getTime() - unhealthySince.getTime() >= armMs) {
      lostAt = unhealthySince;
      armed = true;
    }
  } else if (lostAt !== null || unhealthySince !== null) {
    if (healthySince === null) healthySince = now;
    if (now.getTime() - healthySince.getTime() >= clearMs) {
      if (lostAt !== null) clearedNotifiedAt = true;
      lostAt = null;
      unhealthySince = null;
      healthySince = null;
    }
  }
  return { lostAt, unhealthySince, healthySince, clearedNotifiedAt, armed };
}
var billingSummarySchema = z12.object({
  spend: spendUnderManagementSchema,
  /**
   * The PROJECTED band for the open month (from `spend.projectedTotal`) — the
   * live estimate of where the month will settle. Informational only: the bill is
   * locked at month close from the settled total (see `statements`). Once the
   * month is finalized, `projectedTotal === total`, so this equals `spendToDateBand`.
   */
  band: bandSchema,
  /** The band the month-to-date spend (`spend.total`) is in right now. Shown next
   * to the projected band so "so far → projected" reads clearly; neither is the
   * bill until the month settles. */
  spendToDateBand: bandSchema,
  projectedFee: z12.number().nullable(),
  /** The full band ladder (from `pricingBands()`) so the Billing page can list all
   * tiers and highlight the projected one — rendered from config, never re-typed. */
  pricingBands: z12.array(bandSchema),
  trial: z12.object({
    active: z12.boolean(),
    endsAt: z12.string().nullable(),
    daysLeft: z12.number()
  }),
  statements: z12.array(billingStatementSchema),
  domains: z12.object({ used: z12.number(), max: z12.number() }),
  billingAccess: billingAccessStateSchema,
  // `deferred` = payments not active (Stripe off); `none` = active but no saved card
  // (add one to enable charges); `on_file` = a default card is saved (Phase 2c).
  // `deferred` (Stripe off) | `none` (no card) | `on_file` (+ brand/last4 of the
  // saved card — Stripe exposes only these, never the full number).
  paymentMethod: z12.object({
    status: z12.enum(["deferred", "none", "on_file"]),
    brand: z12.string().nullable().optional(),
    last4: z12.string().nullable().optional()
  }),
  /** Non-prod only: exposes the staging "Simulate charge" tool on the Billing page. */
  simulateChargeAvailable: z12.boolean()
});

// src/notification.ts
import { z as z13 } from "zod";
var notificationSchema = z13.object({
  id: z13.string().uuid(),
  type: z13.string(),
  title: z13.string(),
  body: z13.string(),
  link: z13.string().nullable(),
  read: z13.boolean(),
  createdAt: z13.string()
});
var notificationListSchema = z13.object({
  notifications: z13.array(notificationSchema),
  unread: z13.number().int().nonnegative()
});

// src/feature-flag.ts
import { z as z14 } from "zod";
var featureFlagKeySchema = z14.string().min(1).max(80).regex(
  /^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/,
  "Use lowercase letters, digits, dot, dash, underscore"
);
var FEATURE_FLAGS = {
  "lab-demo": {
    name: "Lab demo toggle",
    description: "A harmless no-op flag for exercising the rollout machinery end to end.",
    owner: "platform"
  },
  compliance: {
    name: "NIS2 compliance lens",
    description: "The compliance & resilience lens on the graph: guardrails on cost recommendations, the per-domain Compliance page (posture + gaps), and the dashboard posture widgets.",
    owner: "cost-optimization"
  }
};
var FEATURE_FLAG_KEYS = Object.keys(FEATURE_FLAGS);
var enabledFeaturesSchema = z14.object({
  features: z14.record(z14.boolean())
});
var featureFlagStateSchema = z14.enum(["off", "limited", "on"]);
var featureFlagOverrideSchema = z14.object({
  organizationId: z14.string().uuid(),
  organizationName: z14.string(),
  /** 'on' = force-enabled (pinned), 'off' = force-disabled (excluded). */
  mode: z14.enum(["on", "off"])
});
var adminFeatureFlagSchema = z14.object({
  key: z14.string(),
  name: z14.string(),
  description: z14.string(),
  owner: z14.string(),
  state: featureFlagStateSchema,
  /** The sticky high-water-mark percentage (0–100); null when Off/On. */
  rolloutPercentage: z14.number().nullable(),
  overrides: z14.array(featureFlagOverrideSchema)
});
var adminFeatureFlagListSchema = z14.object({
  flags: z14.array(adminFeatureFlagSchema),
  /** Total active (non-sandbox is NOT excluded) organizations, for cohort math. */
  organizationCount: z14.number()
});
var setFeatureStateSchema = z14.object({ state: z14.enum(["off", "on"]) });
var setFeatureRolloutSchema = z14.object({ percentage: z14.number().int().min(0).max(100) });
var setFeatureOverrideSchema = z14.object({
  organizationId: z14.string().uuid(),
  mode: z14.enum(["on", "off"])
});
var featureCohortMemberSchema = z14.object({
  organizationId: z14.string().uuid(),
  organizationName: z14.string(),
  reason: z14.enum(["ga", "pinned", "cohort"])
});
var featureCohortSchema = z14.object({
  total: z14.number(),
  /** Capped sample (large GA cohorts aren't fully enumerated). */
  members: z14.array(featureCohortMemberSchema),
  truncated: z14.boolean()
});

// src/audit.ts
import { z as z15 } from "zod";
var auditEventSchema = z15.object({
  id: z15.string().uuid(),
  action: z15.string(),
  /** Resolved actor email, or null for system/pre-tenant events. */
  actor: z15.string().nullable(),
  resourceType: z15.string().nullable(),
  resourceId: z15.string().nullable(),
  metadata: z15.record(z15.unknown()).nullable(),
  createdAt: z15.string()
});

// src/graph.ts
import { z as z16 } from "zod";
var knowledgeRelationshipTypeSchema = z16.enum([
  // Structural (emitted during ingestion, within a single source)
  "CONTAINS",
  "DEPENDS_ON",
  "ATTACHED_TO",
  "BELONGS_TO",
  // Cross-source correlation (discovered across sources, KG-S4)
  "OWNS",
  "PROVISIONS",
  "RUNS_ON"
]);
var knowledgeNodeSchema = z16.object({
  id: z16.string().uuid(),
  provider: integrationProviderSchema,
  type: z16.string(),
  externalId: z16.string(),
  name: z16.string(),
  properties: z16.record(z16.unknown()),
  version: z16.number().int(),
  stale: z16.boolean()
});
var knowledgeEdgeSchema = z16.object({
  id: z16.string().uuid(),
  from: z16.string().uuid(),
  to: z16.string().uuid(),
  type: z16.string(),
  properties: z16.record(z16.unknown()),
  confidence: z16.number(),
  status: z16.string()
});
var knowledgeGraphStatsSchema = z16.object({
  // `nodeCount`/`edgeCount` are the RETURNED (rendered) counts. `totalNodeCount` is the
  // true number of live nodes in the domain; when it exceeds what was returned the
  // payload was capped and `truncated` is true, so the Explorer can say "showing N of M"
  // instead of silently implying it shows everything (scalability M2). Optional so a
  // newer web bundle tolerates an older API that doesn't send them yet.
  nodeCount: z16.number().int(),
  edgeCount: z16.number().int(),
  totalNodeCount: z16.number().int().optional(),
  truncated: z16.boolean().optional(),
  byType: z16.record(z16.number().int()),
  graphVersion: z16.number().int()
});
var knowledgeGraphSchema = z16.object({
  nodes: z16.array(knowledgeNodeSchema),
  edges: z16.array(knowledgeEdgeSchema),
  stats: knowledgeGraphStatsSchema
});
var knowledgeNodeDetailSchema = knowledgeNodeSchema.extend({
  provenance: z16.record(z16.unknown()),
  lastSeenAt: z16.string(),
  createdAt: z16.string(),
  updatedAt: z16.string()
});
var graphNeighborsSchema = z16.object({
  node: knowledgeNodeSchema,
  neighbors: z16.array(knowledgeNodeSchema),
  edges: z16.array(knowledgeEdgeSchema)
});
var graphSearchResultSchema = z16.object({
  nodes: z16.array(knowledgeNodeSchema),
  truncated: z16.boolean()
});
var graphPathSchema = z16.object({
  found: z16.boolean(),
  nodes: z16.array(knowledgeNodeSchema),
  edges: z16.array(knowledgeEdgeSchema)
});
var costSourceSchema = z16.enum(["estimate", "usage-based", "billed"]);
var ownerCostSchema = z16.object({
  ownerId: z16.string().uuid(),
  ownerName: z16.string(),
  monthlyCost: z16.number()
});
var unattributedResourceSchema = z16.object({
  nodeId: z16.string(),
  name: z16.string(),
  type: z16.string(),
  monthlyCost: z16.number()
});
var costAttributionSchema = z16.object({
  currency: z16.string(),
  totalMonthlyCost: z16.number(),
  attributedMonthlyCost: z16.number(),
  unattributedMonthlyCost: z16.number(),
  owners: z16.array(ownerCostSchema),
  unattributedResources: z16.array(unattributedResourceSchema)
});
var estimateConfidenceLevelSchema = z16.enum(["low", "medium", "high", "unknown"]);
var estimateConfidenceSchema = z16.object({
  level: estimateConfidenceLevelSchema,
  estimatedMonthlyCost: z16.number(),
  billedMonthlyCost: z16.number().nullable()
});
var metricKeySchema = z16.enum([
  "cost.monthly",
  // estimated monthly cost (resource or domain total)
  "cost.billed",
  // real billed monthly cost (domain total)
  "cpu.pct",
  // CPU utilization % (window average)
  "cpu.peak",
  // CPU busiest-hour peak for the day (max hourly Maximum over 24h) — frequency-aware gating (TD-054)
  "mem.pct",
  // memory utilization %
  "gpu.pct",
  // GPU / accelerator utilization % (window average) — AI-infra optimization (AI-0)
  "gpu.peak",
  // GPU busiest-hour peak for the day — frequency-aware gating, mirrors cpu.peak (AI-0)
  "gpu.mem.pct",
  // GPU memory (VRAM) utilization % — right-accelerator sizing (AI-0)
  "throughput",
  // requests/invocations per unit time
  "utilization.score"
  // the 1–5 domain utilization score
]);
var observationSourceSchema = z16.enum([
  "estimate",
  "usage-based",
  "billed",
  "cloudwatch",
  "computed"
]);
var metricPointSchema = z16.object({
  day: z16.string(),
  value: z16.number()
});
var domainHistorySchema = z16.object({
  costMonthly: z16.array(metricPointSchema),
  billedMonthly: z16.array(metricPointSchema),
  utilizationScore: z16.array(metricPointSchema)
});
var resourceHistorySchema = z16.object({
  costMonthly: z16.array(metricPointSchema),
  cpuPct: z16.array(metricPointSchema),
  memPct: z16.array(metricPointSchema)
});
var providerCostTrendSchema = z16.object({
  provider: z16.string(),
  accountCount: z16.number(),
  points: z16.array(metricPointSchema)
});
var typeCostTrendSchema = z16.object({
  type: z16.string(),
  points: z16.array(metricPointSchema)
});
var costTrendsSchema = z16.object({
  total: z16.array(metricPointSchema),
  providers: z16.array(providerCostTrendSchema),
  types: z16.array(typeCostTrendSchema)
});
var resourceExplanationSchema = z16.object({
  nodeId: z16.string(),
  content: z16.string(),
  model: z16.string(),
  generatedAt: z16.string()
});
var cachedExplanationSchema = resourceExplanationSchema.nullable();
var unbilledCloudSchema = z16.object({
  integrationId: z16.string(),
  provider: z16.string(),
  displayName: z16.string()
});
var domainAssessmentSchema = z16.object({
  domainId: z16.string(),
  /** 1–5 (5 best), or null when no resource has usable usage metrics. */
  utilizationScore: z16.number().nullable(),
  /** Share of *scoreable* monthly cost that carried a usage metric (0–1). */
  coverage: z16.number(),
  assessedResources: z16.number().int(),
  /** Cost-bearing resources tracked by traffic/state rather than a utilization
   * score (NAT, load balancers, storage, Lambda) — actionable via
   * recommendations, but not part of the 1–5 score or `coverage`. */
  trackedResources: z16.number().int(),
  /** The reconciled ESTIMATE of monthly cost (list prices nudged toward billed). */
  totalMonthlyCost: z16.number(),
  /** The domain's real BILLED cost for the current calendar month, MONTH-TO-DATE,
   * from the billing-grade ledger — or null when no billing data exists yet. When
   * present, the UI leads with this and treats `totalMonthlyCost` as the fallback. */
  billedMonthlyCost: z16.number().nullable(),
  /** The projected FULL-month billed cost (run-rate, or the prior month early on),
   * or null when there's no billed signal. Shown alongside the month-to-date figure. */
  projectedBilledCost: z16.number().nullable(),
  /** Itemization of the "Other services" bucket — billed spend NOT attributed to a
   * modeled resource, broken down by the cloud's billed-service name (projected to a
   * full month), largest first. Lets the UI explain the remainder (tax, CloudWatch,
   * data transfer, services we don't model individually) instead of one opaque blob.
   * Empty when there's no billed signal. Σ(amount) reconciles to
   * `projectedBilledCost − Σ(billed resources)`. */
  otherServices: z16.array(z16.object({ service: z16.string(), amount: z16.number() })).optional(),
  /** Connected cloud accounts that currently contribute NO billed cost to the total —
   * their billing feed isn't set up yet, is still in its grace period, or is warming
   * up. We never substitute estimates for billed cost; instead the UI calls these out
   * ("N integrations missing billed cost — not in this total") wherever billed cost is
   * shown, so the total stays a pure billed figure. */
  unbilledClouds: z16.array(unbilledCloudSchema),
  /** Placeholder until the recommendations engine lands. */
  costOptimizationActions: z16.number().int()
});
var capabilityFileSchema = z16.object({
  path: z16.string(),
  snippet: z16.string()
});
var capabilityMapEntrySchema = z16.object({
  capability: z16.string(),
  files: z16.array(capabilityFileSchema),
  contentHash: z16.string()
});

// src/cost.ts
import { z as z17 } from "zod";
var TYPE_CATEGORY = {
  "compute.instance": "Compute",
  "compute.scaleset": "Compute",
  "compute.function": "Compute",
  "compute.workload": "Compute",
  "ml.endpoint": "AI / ML",
  "ml.notebook": "AI / ML",
  "ml.model": "AI / ML",
  database: "Databases",
  "warehouse.instance": "Analytics",
  "cache.cluster": "Cache",
  "storage.volume": "Storage",
  "storage.snapshot": "Storage",
  "storage.bucket": "Storage",
  "network.nat": "Networking",
  "network.address": "Networking",
  "network.loadbalancer": "Networking",
  "network.endpoint": "Networking",
  "network.gateway": "Networking",
  "container.cluster": "Compute",
  "container.workload": "Compute",
  "search.cluster": "Databases",
  "streaming.cluster": "Analytics",
  "storage.filesystem": "Storage",
  "network.cdn": "Networking",
  "cloud.account": "Account"
};
var categoryOf = (type) => TYPE_CATEGORY[type] ?? type;
var costOf = (p) => typeof p.monthlyCost === "number" ? p.monthlyCost : 0;
var costProvenanceOf = (p) => p.costProvenance === "billed" ? "billed" : "estimated";
var costBreakdownResourceSchema = z17.object({
  nodeId: z17.string(),
  name: z17.string(),
  type: z17.string(),
  provider: z17.string(),
  /** Provider-native id — enough to build a deep console link (with region). */
  externalId: z17.string(),
  /** Cloud region, for the console deep link (null when not applicable). */
  region: z17.string().nullable(),
  monthlyCost: z17.number(),
  /** Calibrated to billed spend (vs a list-price estimate). */
  billed: z17.boolean(),
  /** Attributed to an owning repo/app via an OWNS edge (else it's "unattributed"). */
  owned: z17.boolean()
});
var costBreakdownCategorySchema = z17.object({
  category: z17.string(),
  total: z17.number(),
  /** Every itemized resource in this category is billed-calibrated. */
  billed: z17.boolean(),
  /** Exact count of itemized resources (may exceed `topResources.length`). */
  resourceCount: z17.number(),
  /** Top resources by cost, for the drill-down; the tail rolls into "Other (N)". */
  topResources: z17.array(costBreakdownResourceSchema)
});
var costBreakdownProviderSchema = z17.object({
  provider: z17.string(),
  total: z17.number(),
  billed: z17.boolean(),
  categories: z17.array(costBreakdownCategorySchema)
});
var costBreakdownSchema = z17.object({
  currency: z17.string(),
  /** The headline total: billed-projected in billed mode, else the summed estimate. */
  totalMonthlyCost: z17.number(),
  /** Sum of the itemized resources (Σ providers). */
  attributedMonthlyCost: z17.number(),
  /** Billed spend not itemized to a resource (== totalMonthlyCost − attributed) in
   * billed mode; 0 otherwise. Reconciles "Overall" to the headline. */
  otherServicesTotal: z17.number(),
  provenance: z17.enum(["billed", "estimated"]),
  /** Exact count of itemized (costed) resources across the domain. */
  resourceCount: z17.number(),
  providers: z17.array(costBreakdownProviderSchema),
  /** Itemized "Other services" billed line items (tax, data transfer, …). */
  otherServices: z17.array(z17.object({ service: z17.string(), amount: z17.number() })),
  /** Connected clouds with resources but no billed cost in the total yet. */
  unbilledClouds: z17.array(unbilledCloudSchema)
});

// src/recommendations.ts
import { z as z19 } from "zod";

// src/compliance.ts
import { z as z18 } from "zod";
var complianceFrameworkSchema = z18.enum(["nis2", "iso27001", "bsi-c5", "dora", "soc2"]);
var complianceControlIdSchema = z18.enum([
  "encryption-at-rest",
  "backup-coverage",
  "dr-redundancy",
  "public-exposure",
  "supported-versions",
  "log-retention",
  "data-residency"
]);
var controlStatusSchema = z18.enum(["met", "gap", "na"]);
var gapSeveritySchema = z18.enum(["critical", "high", "medium"]);
var controlGateSchema = z18.enum(["block", "warn", "off"]);
var NIS2_CONTROLS = [
  {
    id: "encryption-at-rest",
    label: "Encryption at rest",
    nis2Measure: "Cryptography & encryption",
    description: "Storage, databases and volumes are encrypted at rest (KMS).",
    severity: "critical",
    weight: 3,
    signalAvailableToday: false
  },
  {
    id: "backup-coverage",
    label: "Backup coverage",
    nis2Measure: "Business continuity, backup & crisis management",
    description: "Critical data stores have a backup/retention policy in place.",
    severity: "critical",
    weight: 3,
    signalAvailableToday: false
  },
  {
    id: "dr-redundancy",
    label: "Failover & redundancy",
    nis2Measure: "Business continuity, backup & crisis management",
    description: "Production data stores run Multi-AZ / replicated for failover.",
    severity: "high",
    weight: 2,
    signalAvailableToday: false
  },
  {
    id: "public-exposure",
    label: "Network exposure",
    nis2Measure: "Access control & asset management",
    description: "No resource is needlessly exposed to the public internet.",
    severity: "critical",
    weight: 3,
    signalAvailableToday: true
  },
  {
    id: "supported-versions",
    label: "Supported versions",
    nis2Measure: "Secure maintenance & vulnerability handling",
    description: "Engines and runtimes are on supported, non-EOL versions.",
    severity: "high",
    weight: 2,
    signalAvailableToday: true
  },
  {
    id: "log-retention",
    label: "Log retention",
    nis2Measure: "Incident handling & logging",
    description: "Audit/flow logs are retained for at least the required window.",
    severity: "medium",
    weight: 1,
    signalAvailableToday: false
  },
  {
    id: "data-residency",
    label: "Data residency",
    nis2Measure: "Supply-chain security & residency",
    description: "Data-bearing resources sit in an approved (EU) region.",
    severity: "high",
    weight: 2,
    signalAvailableToday: true
  }
];
var NIS2_CONTROL_BY_ID = Object.fromEntries(NIS2_CONTROLS.map((c) => [c.id, c]));
var complianceGuardrailFlagSchema = z18.object({
  controlId: complianceControlIdSchema,
  controlLabel: z18.string(),
  nis2Measure: z18.string(),
  /** `block` = the domain policy enforces this control; `warn` = advisory. */
  gate: z18.enum(["block", "warn"]),
  /** Plain-language: what the change would do to the control. */
  message: z18.string(),
  /** The compliant way to still get (most of) the saving, when one exists. */
  compliantAlternative: z18.string().nullable(),
  /** Estimated $/mo of the compliant alternative — less than the original saving.
   * Shown as the headline once the customer accepts the alternative. Null when the
   * alternative has no distinct estimate (e.g. a backup kept, not a cost action). */
  compliantAlternativeSaving: z18.number().nullish(),
  /** A short basis for the alternative's saving (e.g. "Downsize one class, keep
   * Multi-AZ"), shown beside the re-estimated headline. */
  compliantAlternativeBasis: z18.string().nullish()
});
var complianceGapSchema = z18.object({
  id: z18.string(),
  controlId: complianceControlIdSchema,
  controlLabel: z18.string(),
  nis2Measure: z18.string(),
  severity: gapSeveritySchema,
  /** The affected asset. */
  nodeExternalId: z18.string(),
  resourceName: z18.string(),
  resourceType: z18.string(),
  provider: z18.string(),
  region: z18.string().nullable(),
  /** What's wrong, in one line (e.g. "Backup retention is 0 days"). */
  summary: z18.string(),
  /** Short, conservative remediation guidance (the "Fix" content). */
  remediation: z18.string(),
  /** Whether a ready-to-apply remediation recommendation is linked. */
  fixAvailable: z18.boolean(),
  /** The remediation recommendation id, when one is linked (rides the recs
   * lifecycle), else null. */
  recommendationId: z18.string().nullable(),
  /** An existing cost-optimization recommendation on the SAME resource, when one
   * is open — so the Compliance gap and the optimization cross-reference each
   * other. Null when the resource has no open recommendation. */
  relatedRecommendationId: z18.string().nullish(),
  /** When this gap first appeared in the snapshot history (ISO) — shown as
   * "detected <when>" on the row. Null when it predates snapshots / none exist. */
  firstDetectedAt: z18.string().nullish(),
  /** True when the gap appeared since the previous snapshot — drives the "New"
   * badge so a reader spots freshly-introduced gaps without re-reading the list. */
  isNew: z18.boolean().nullish()
});
var controlPostureSchema = z18.object({
  controlId: complianceControlIdSchema,
  label: z18.string(),
  nis2Measure: z18.string(),
  /** 0–100: share of applicable resources that meet the control. */
  score: z18.number(),
  met: z18.number(),
  gaps: z18.number(),
  /** Resources this control doesn't apply to (excluded from the score). */
  na: z18.number(),
  gate: controlGateSchema
});
var compliancePostureSchema = z18.object({
  enabled: z18.boolean(),
  framework: complianceFrameworkSchema,
  /** 0–100 weighted overall posture across the controls in scope. */
  score: z18.number(),
  /** Month-over-month delta, or null when there's no prior point. */
  scoreTrend: z18.number().nullable(),
  assetsInScope: z18.number(),
  gapCount: z18.number(),
  criticalGapCount: z18.number(),
  controls: z18.array(controlPostureSchema),
  /** The highest-risk gaps, ranked — the dashboard widget shows the top few. */
  topGaps: z18.array(complianceGapSchema),
  generatedAt: z18.string()
});
var complianceSummarySchema = z18.object({
  posture: compliancePostureSchema,
  gaps: z18.array(complianceGapSchema)
});
var domainComplianceSchema = z18.object({
  domainId: z18.string(),
  name: z18.string(),
  score: z18.number(),
  gapCount: z18.number(),
  criticalGapCount: z18.number()
});
var orgCompliancePostureSchema = z18.object({
  /** True when at least one readable domain has compliance enabled. */
  enabled: z18.boolean(),
  /** Weighted overall score across compliance-enabled domains (0–100). */
  score: z18.number(),
  domainsEnabled: z18.number(),
  gapCount: z18.number(),
  criticalGapCount: z18.number(),
  domains: z18.array(domainComplianceSchema)
});
var evidenceAssetControlSchema = z18.object({
  controlId: complianceControlIdSchema,
  label: z18.string(),
  status: controlStatusSchema
  // 'met' | 'gap' — 'na' never appears per asset
});
var evidenceAssetSchema = z18.object({
  nodeExternalId: z18.string(),
  resourceName: z18.string(),
  resourceType: z18.string(),
  provider: z18.string(),
  region: z18.string().nullable(),
  metCount: z18.number(),
  gapCount: z18.number(),
  controls: z18.array(evidenceAssetControlSchema)
});
var evidencePolicyLineSchema = z18.object({
  controlId: complianceControlIdSchema,
  label: z18.string(),
  nis2Measure: z18.string(),
  gate: controlGateSchema
});
var complianceEvidencePackSchema = z18.object({
  metadata: z18.object({
    generatedAt: z18.string(),
    framework: complianceFrameworkSchema,
    frameworkReference: z18.string(),
    organizationId: z18.string(),
    organizationName: z18.string(),
    domainId: z18.string(),
    domainName: z18.string(),
    /** The data-residency region the pack was generated in (EU residency). */
    region: z18.string(),
    generatedByEmail: z18.string().nullable(),
    disclaimer: z18.string()
  }),
  posture: compliancePostureSchema,
  policy: z18.array(evidencePolicyLineSchema),
  gaps: z18.array(complianceGapSchema),
  assetRegister: z18.array(evidenceAssetSchema)
});
var COMPLIANCE_EVIDENCE_DISCLAIMER = "This pack is operational evidence of the cloud technical risk-management measures (NIS2 Art. 21 / \xA730) that Lizrd continuously observes from read-only cloud metadata. It is a point-in-time record to support board oversight, auditors, and supplier questionnaires \u2014 it is NOT a NIS2 certification, an audit opinion, or legal advice. Organisational and procedural measures are out of scope.";
var COMPLIANCE_FRAMEWORK_REFERENCE = {
  nis2: "NIS2 Directive (EU) 2022/2555 \u2014 Art. 21 risk-management measures (DE: BSIG \xA730)",
  iso27001: "ISO/IEC 27001:2022",
  "bsi-c5": "BSI Cloud Computing Compliance Criteria Catalogue (C5:2020)",
  dora: "Regulation (EU) 2022/2554 (DORA)",
  soc2: "AICPA SOC 2 \u2014 Trust Services Criteria"
};
var snapshotControlSchema = z18.object({
  controlId: complianceControlIdSchema,
  score: z18.number(),
  met: z18.number(),
  gaps: z18.number()
});
var snapshotGapSchema = z18.object({
  controlId: complianceControlIdSchema,
  controlLabel: z18.string(),
  severity: gapSeveritySchema,
  nodeExternalId: z18.string(),
  resourceName: z18.string(),
  resourceType: z18.string(),
  provider: z18.string(),
  summary: z18.string()
});
var complianceSnapshotSchema = z18.object({
  id: z18.string(),
  domainId: z18.string(),
  framework: complianceFrameworkSchema,
  score: z18.number(),
  gapCount: z18.number(),
  criticalGapCount: z18.number(),
  assetsInScope: z18.number(),
  controls: z18.array(snapshotControlSchema),
  trigger: z18.enum(["scheduled", "manual", "sync"]),
  createdAt: z18.string()
});
var scoreTrendPointSchema = z18.object({
  date: z18.string(),
  score: z18.number()
});
var complianceDriftSchema = z18.object({
  /** True when there is a prior snapshot to compare against. */
  hasPrior: z18.boolean(),
  /** The prior snapshot's time, null when there's no prior. */
  since: z18.string().nullable(),
  /** The latest snapshot's time, null when none exists yet. */
  current: z18.string().nullable(),
  /** latest.score − prior.score (0 when no prior). */
  scoreDelta: z18.number(),
  /** Gaps present now but not in the prior snapshot — drift from the baseline. */
  newGaps: z18.array(snapshotGapSchema),
  /** Gaps present in the prior snapshot but resolved now — improvements. */
  resolvedGaps: z18.array(snapshotGapSchema)
});
var complianceTrendSchema = z18.object({
  series: z18.array(scoreTrendPointSchema),
  drift: complianceDriftSchema
});
var complianceSettingsSchema = z18.object({
  enabled: z18.boolean(),
  framework: complianceFrameworkSchema,
  controls: z18.record(complianceControlIdSchema, controlGateSchema)
});
var updateComplianceSettingsSchema = z18.object({
  enabled: z18.boolean().optional(),
  framework: complianceFrameworkSchema.optional(),
  controls: z18.record(complianceControlIdSchema, controlGateSchema).optional()
}).refine((v) => v.enabled !== void 0 || v.framework !== void 0 || v.controls !== void 0, {
  message: "Provide at least one field to update."
});
function defaultControlGate(controlId) {
  if (controlId === "data-residency") return "off";
  const def = NIS2_CONTROL_BY_ID[controlId];
  return def.severity === "medium" ? "warn" : "block";
}

// src/recommendations.ts
var recommendationCategorySchema = z19.enum([
  "orphaned",
  "idle",
  "rightsize",
  "modernize",
  "storage",
  "network",
  "reliability",
  "commitment",
  "schedule",
  "autoscale",
  "ai"
  // AI/LLM token economics — prompt caching, Batch API, model routing (Family C)
]);
function recommendationDirection(category) {
  return category === "reliability" ? "increase" : "reduce";
}
var recommendationSeveritySchema = z19.enum(["low", "medium", "high"]);
var recommendationPriorityTierSchema = z19.enum(["high", "medium", "low"]);
var recommendationEffortLevelSchema = z19.enum([
  "very_low",
  "low",
  "medium",
  "high",
  "very_high"
]);
var recommendationEvidenceSchema = z19.object({
  label: z19.string(),
  value: z19.string()
});
var recommendationCodeSchema = z19.object({
  repo: z19.string(),
  file: z19.string().nullable(),
  resourceName: z19.string().nullable(),
  /** The cloud↔IaC link was confirmed from the deploy tool's own state (TD-058) —
   * an exact, authoritative match, not a heuristic. Drives a "verified from state"
   * trust badge. Absent/false = heuristic or AI-proposed correlation. */
  verifiedFromState: z19.boolean().optional()
});
var recommendationStatusSchema = z19.enum([
  "open",
  "accepted",
  "completed",
  "validated",
  "failed_validation",
  "rejected",
  "superseded",
  "snoozed",
  "dismissed"
]);
var validationOutcomeSchema = z19.enum(["full", "partial"]);
var validationAttributionSchema = z19.enum(["executed", "self_resolved"]);
var recommendationNarrationSchema = z19.object({
  content: z19.string(),
  model: z19.string(),
  generatedAt: z19.string()
});
var recommendationRiskLevelSchema = z19.enum(["low", "medium", "high"]);
var recommendationGuidanceSchema = z19.object({
  /** (a) A deeper, plain-language explanation of what the change is and why. */
  actionExplanation: z19.string(),
  /** (b) Risk of making the change, given what we know. */
  riskLevel: recommendationRiskLevelSchema,
  riskSummary: z19.string(),
  /** (c) The exact change to apply, step by step, in full sentences. */
  applySteps: z19.array(z19.string()),
  /** (d) The exact diff to apply, when the change is code-expressible; else null. */
  diff: z19.string().nullable(),
  /** (e) What to watch for while / after making the change. */
  watchFor: z19.array(z19.string()),
  /** (f) How to roll the change back. */
  rollback: z19.array(z19.string()),
  /** Server-stamped (never model-authored): the change permanently deletes the
   * resource. Drives the UI's "Permanent removal" notice and the high-risk floor.
   * Always present in API responses (the server stamps it before returning). */
  isRemoval: z19.boolean(),
  model: z19.string(),
  generatedAt: z19.string()
});
var recommendationGuidanceContentSchema = recommendationGuidanceSchema.omit({ model: true, generatedAt: true }).extend({ isRemoval: z19.boolean().default(false) });
var recommendationSchema = z19.object({
  id: z19.string(),
  nodeId: z19.string().nullable(),
  nodeExternalId: z19.string(),
  resourceName: z19.string(),
  resourceType: z19.string(),
  provider: z19.string(),
  category: recommendationCategorySchema,
  title: z19.string(),
  rationale: z19.string(),
  estimatedMonthlySaving: z19.number(),
  savingConfidence: estimateConfidenceLevelSchema,
  /** Plain-language "why we're this confident" — the corroborating signals. */
  confidenceReason: z19.string(),
  /** How the saving was derived (e.g. "Downsize one size (~50%)"). */
  savingsBasis: z19.string(),
  severity: recommendationSeveritySchema,
  /** Estimated implementation effort (EST) — a standardized level plus a
   * representative time in minutes, computed deterministically and independently
   * of the saving amount. */
  effortLevel: recommendationEffortLevelSchema,
  effortMinutes: z19.number(),
  /** Deterministic combined value-vs-effort priority (PRI): a numeric score (sort
   * key, internal) and its derived tier. `userPriorityTier` is a manual override
   * that does not affect the computed score; the effective tier is
   * `userPriorityTier ?? priorityTier`. */
  priorityScore: z19.number(),
  priorityTier: recommendationPriorityTierSchema,
  userPriorityTier: recommendationPriorityTierSchema.nullable(),
  evidence: z19.array(recommendationEvidenceSchema),
  suggestedAction: z19.string(),
  /** Assigned owner (ACT-006), or null when unassigned. `assignedToName` is the
   * display name; `assignedToId` identifies the member for filtering/avatars. */
  assignedToId: z19.string().nullable(),
  assignedToName: z19.string().nullable(),
  /** Owning repo / Terraform file, or null when not correlated. */
  code: recommendationCodeSchema.nullable(),
  status: recommendationStatusSchema,
  /** When the opportunity was first discovered — the item's age in the pipeline. */
  firstSeenAt: z19.string(),
  snoozedUntil: z19.string().nullable(),
  /** Who executed the optimization and when — set from `completed` onward. */
  executedByName: z19.string().nullable(),
  executedAt: z19.string().nullable(),
  /** The resource's monthly cost captured at execution — the baseline realized
   * savings are measured against (null until executed). */
  baselineMonthlyCost: z19.number().nullable(),
  /** Measured monthly saving once validated (baseline − current); null otherwise. */
  realizedMonthlySaving: z19.number().nullable(),
  validatedAt: z19.string().nullable(),
  validationOutcome: validationOutcomeSchema.nullable(),
  /** How the saving was credited — executed-through-Lizrd vs detected-resolved
   * out-of-band. Null until validated. */
  validationAttribution: validationAttributionSchema.nullable(),
  /** Plain-language evidence behind the validation result (why it did/didn't confirm). */
  validationEvidence: z19.string().nullable(),
  /** Cached AI narration, or null until generated. */
  narration: recommendationNarrationSchema.nullable(),
  /** Whether implementation guidance has been generated (fetched on demand). */
  hasGuidance: z19.boolean(),
  /** Whether the current user has opened this recommendation at least once
   * (drives the "New" badge — it clears on first open). Per-user, resolved in a
   * single batched lookup on list so it adds no per-card latency. */
  viewed: z19.boolean(),
  /** Compliance guardrails raised against this cost action (NIS2 G2). Present
   * only when the domain has compliance enabled and the action would weaken a
   * control; `.nullish()` (additive) so an older API's response still parses
   * during rollout. */
  complianceFlags: z19.array(complianceGuardrailFlagSchema).nullish(),
  /** True when the current guidance was generated from the NIS2-compliant
   * alternative (the customer accepted it from a guardrail). Drives the collapsed
   * "alternative applied" state on the guardrail banner. `.nullish()` (additive). */
  complianceAlternativeApplied: z19.boolean().nullish()
});
var recommendationMetricsSchema = z19.object({
  /** 7-day average CPU / memory utilization (%). */
  cpuPct: z19.number().nullable(),
  memPct: z19.number().nullable(),
  /** Busiest-hour CPU / memory over the window (%) — so a right-size stays safe
   * at peak, not just on average. */
  peakCpuPct: z19.number().nullable(),
  peakMemPct: z19.number().nullable(),
  throughput: z19.object({ value: z19.number(), unit: z19.string() }).nullable(),
  /** RDS free memory (MB) / ElastiCache hit rate (%) where applicable. */
  freeMemMb: z19.number().nullable(),
  hitRate: z19.number().nullable(),
  /** Instance I/O + network signals used as a downsize-safety veto: a CPU-idle box
   * can be correctly sized for disk or network throughput (ceilings scale with
   * instance size), so shrinking it would throttle it. Peak over the window.
   * `.nullish()` (additive) so an older API's response still parses during rollout. */
  netMbps: z19.number().nullish(),
  diskIops: z19.number().nullish(),
  diskMbps: z19.number().nullish(),
  /** Provider-native "hit the disk-I/O ceiling" flag (e.g. Azure consumed % near
   * 100%). null when the provider exposes no direct ceiling signal. */
  ioSaturated: z19.boolean().nullish(),
  /** The measurement window, e.g. "7d". */
  window: z19.string()
}).nullable();
var recommendationsSchema = z19.object({
  currency: z19.string(),
  totalMonthlySaving: z19.number(),
  count: z19.number(),
  realizedMonthlySaving: z19.number(),
  validatedCount: z19.number(),
  items: z19.array(recommendationSchema),
  /** True when the domain has a graph but recommendations haven't been computed
   * yet, so a recompute was kicked off in the background (never inline in the
   * request). The client shows a "generating…" state and polls until it clears.
   * Optional so an older API during a rollout (which omits it) reads as
   * not-computing on the client (`?.computing` → undefined → falsy). */
  computing: z19.boolean().optional()
});
var domainSavingsSchema = z19.object({
  domainId: z19.string(),
  name: z19.string(),
  /** Open opportunity (sum of estimated monthly saving over open items). */
  estimatedMonthlySaving: z19.number(),
  /** Proven value (sum of realized monthly saving over validated items). */
  realizedMonthlySaving: z19.number(),
  openCount: z19.number(),
  validatedCount: z19.number()
});
var orgSavingsSummarySchema = z19.object({
  currency: z19.string(),
  totals: z19.object({
    estimatedMonthlySaving: z19.number(),
    realizedMonthlySaving: z19.number(),
    openCount: z19.number(),
    validatedCount: z19.number()
  }),
  domains: z19.array(domainSavingsSchema)
});
var savingsReconciliationStatusSchema = z19.enum([
  "corroborated",
  "partially_corroborated",
  "offset_by_growth",
  "insufficient_data"
]);
var savingsReconciliationSchema = z19.object({
  currency: z19.string(),
  status: savingsReconciliationStatusSchema,
  /** Our validated realized run-rate ($/mo), deduped per resource. */
  attributedRealizedMonthly: z19.number(),
  /** Net change in billed spend baseline→current ($/mo); negative = the bill fell.
   * Null when there aren't two finalized months to compare. */
  billedMonthlyChange: z19.number().nullable(),
  /** attributed − billedReduction: positive = new/grown spend offset some savings;
   * negative = the bill fell by even more than we attributed. Null when insufficient. */
  impliedGrowthMonthly: z19.number().nullable(),
  /** The finalized months compared (earliest → latest in the trailing window). */
  baselineMonth: z19.string().nullable(),
  currentMonth: z19.string().nullable(),
  /** Plain-language, honest summary for the UI. */
  note: z19.string()
});
var recommendationEventSchema = z19.object({
  id: z19.string(),
  type: z19.string(),
  fromStatus: recommendationStatusSchema.nullable(),
  toStatus: recommendationStatusSchema.nullable(),
  actorName: z19.string().nullable(),
  /** Short human-readable summary of the event (e.g. realized saving, evidence). */
  detail: z19.string().nullable(),
  createdAt: z19.string()
});
var recommendationHistorySchema = z19.object({
  events: z19.array(recommendationEventSchema)
});
var rejectionReasonSchema = z19.enum([
  "required_as_is",
  "too_risky",
  "not_worth_effort",
  "already_addressed",
  "inaccurate",
  "not_permitted",
  "other"
]);
var REJECTION_REASON_LABELS = {
  required_as_is: "Resource is needed as-is",
  too_risky: "Too risky to change",
  not_worth_effort: "Not worth the effort / savings too small",
  already_addressed: "Already handled or planned",
  inaccurate: "Recommendation looks inaccurate",
  not_permitted: "Not allowed (policy / compliance)",
  other: "Other"
};
var updateRecommendationSchema = z19.object({
  action: z19.enum(["accept", "reject", "complete", "reopen"]),
  rejectionReason: rejectionReasonSchema.optional(),
  rejectionNote: z19.string().trim().max(500).optional()
}).refine((v) => v.action !== "reject" || !!v.rejectionReason, {
  message: "A rejection reason is required.",
  path: ["rejectionReason"]
});
var guideRecommendationSchema = z19.object({
  alternativeIntent: z19.string().trim().min(1).max(500).optional()
});
var setRecommendationPrioritySchema = z19.object({
  userPriorityTier: recommendationPriorityTierSchema.nullable()
});
var setRecommendationAssigneeSchema = z19.object({
  assignedToId: z19.string().uuid().nullable()
});
var recommendationCommentSchema = z19.object({
  id: z19.string(),
  authorId: z19.string(),
  authorName: z19.string(),
  body: z19.string(),
  createdAt: z19.string()
});
var recommendationCommentsSchema = z19.object({
  comments: z19.array(recommendationCommentSchema)
});
var createRecommendationCommentSchema = z19.object({
  body: z19.string().trim().min(1).max(2e3)
});
var bulkRecommendationOpSchema = z19.discriminatedUnion("type", [
  z19.object({
    type: z19.literal("status"),
    action: z19.enum(["accept", "reject", "complete", "reopen"]),
    rejectionReason: rejectionReasonSchema.optional(),
    rejectionNote: z19.string().trim().max(500).optional()
  }),
  z19.object({ type: z19.literal("assign"), assignedToId: z19.string().uuid().nullable() }),
  z19.object({
    type: z19.literal("priority"),
    userPriorityTier: recommendationPriorityTierSchema.nullable()
  })
]);
var bulkRecommendationSchema = z19.object({
  ids: z19.array(z19.string().uuid()).min(1).max(100),
  op: bulkRecommendationOpSchema
}).refine((v) => v.op.type !== "status" || v.op.action !== "reject" || !!v.op.rejectionReason, {
  message: "A rejection reason is required.",
  path: ["op", "rejectionReason"]
});
var bulkRecommendationResultSchema = z19.object({
  results: z19.array(z19.object({ id: z19.string(), ok: z19.boolean(), error: z19.string().optional() }))
});

// src/review.ts
import { z as z20 } from "zod";
var reviewProviderSchema = z20.enum(["GITHUB", "GITLAB"]);
var reviewFindingKindSchema = z20.enum(["cost", "waste"]);
var reviewSeveritySchema = z20.enum(["low", "medium", "high"]);
var reviewRiskLevelSchema = z20.enum(["low", "medium", "high"]);
var reviewStatusSchema = z20.enum(["open", "merged", "closed"]);
var reviewCommentSchema = z20.object({
  id: z20.string().uuid(),
  reviewId: z20.string().uuid(),
  kind: reviewFindingKindSchema,
  /** The specific lever/category, e.g. 'cost-increase', 'gp2-to-gp3',
   * 'over-provisioned-iops', 'missing-autoscaling'. */
  category: z20.string(),
  severity: reviewSeveritySchema,
  riskLevel: reviewRiskLevelSchema,
  title: z20.string(),
  rationale: z20.string(),
  /** Monthly cost impact in USD: positive = increase, negative = saving, 0 = n/a. */
  costDeltaMonthly: z20.number(),
  /** The changed file the finding sits on (null when repo-wide). */
  filePath: z20.string().nullable(),
  /** Finer grouping within a repo (directory / module), when known. */
  packageKey: z20.string().nullable(),
  /** The cloud resource address the finding resolved to, when known. */
  resourceName: z20.string().nullable(),
  /** Whether the finding has been addressed on the PR (resolved on a later commit). */
  addressed: z20.boolean(),
  createdAt: z20.string()
});
var reviewSchema = z20.object({
  id: z20.string().uuid(),
  domainId: z20.string().uuid(),
  provider: reviewProviderSchema,
  /** owner/repo (GitHub) or group/project path (GitLab) — the code-package group key. */
  repoFullName: z20.string(),
  /** PR number (GitHub) or MR iid (GitLab). */
  prNumber: z20.number().int(),
  title: z20.string(),
  /** Author handle on the SCM, for display. */
  author: z20.string().nullable(),
  /** External URL to the PR/MR (opens on GitHub/GitLab). */
  url: z20.string().url().nullable(),
  /** Head commit the analysis ran against. */
  headSha: z20.string().nullable(),
  status: reviewStatusSchema,
  /** Net monthly cost delta across the PR (USD; positive = increase). */
  costDeltaMonthly: z20.number(),
  createdAt: z20.string(),
  updatedAt: z20.string(),
  comments: z20.array(reviewCommentSchema)
});
var reviewRepoGroupSchema = z20.object({
  repoFullName: z20.string(),
  provider: reviewProviderSchema,
  reviewCount: z20.number().int(),
  commentCount: z20.number().int(),
  /** Net monthly cost delta summed across the group's reviews (USD). */
  costDeltaMonthly: z20.number(),
  reviews: z20.array(reviewSchema)
});
var reviewListSchema = z20.object({
  /** Whether reviews are enabled for this domain (drives the disabled/empty state). */
  enabled: z20.boolean(),
  /** Dev-only: the "simulate a PR review" trigger is available (DEV_SEED_ENABLED). */
  canSimulate: z20.boolean(),
  summary: z20.object({
    reviewCount: z20.number().int(),
    commentCount: z20.number().int(),
    /** How many findings are waste anti-patterns (the rest are cost). */
    wasteCount: z20.number().int(),
    /** Net monthly cost delta across all surfaced reviews (USD). */
    costDeltaMonthly: z20.number()
  }),
  groups: z20.array(reviewRepoGroupSchema)
});
var reviewCommentGroupSchema = z20.enum([
  "cost",
  // projected monthly cost increases
  "waste",
  // avoidable waste / anti-patterns in the change itself
  "usage",
  // measured-utilization checks (scaling up an idle resource, …)
  "recommendations"
  // the change touches a resource with an open Lizrd recommendation
]);
var REVIEW_COMMENT_GROUPS = reviewCommentGroupSchema.options;
var REVIEW_COMMENT_GROUP_CATALOG = [
  {
    key: "cost",
    label: "Cost increases",
    description: "Flag changes that raise your projected monthly bill (above the threshold below)."
  },
  {
    key: "waste",
    label: "Waste & anti-patterns",
    description: "Catch avoidable waste in the change itself \u2014 older volume types, over-provisioned IOPS, a service with no autoscaling, oversized requests, tightened autoscalers."
  },
  {
    key: "usage",
    label: "Usage-aware checks",
    description: "Use measured utilization to flag scaling up a resource that already runs cold \u2014 or shrinking one that looks idle on CPU but is working hard on disk/network I/O."
  },
  {
    key: "recommendations",
    label: "Recommendation cross-links",
    description: "When a change touches a resource Lizrd already recommends acting on \u2014 unused/idle, a stranded commitment, a Graviton move, an off-hours schedule, or networking \u2014 say so."
  }
];
var COMMENT_GROUP_FOR_CATEGORY = {
  "cost-increase": "cost",
  "gp2-to-gp3": "waste",
  "over-provisioned-iops": "waste",
  "missing-autoscaling": "waste",
  "k8s-oversize": "waste",
  "k8s-hpa-floor": "waste",
  "k8s-hpa-ceiling": "waste",
  "k8s-hpa-target": "waste",
  rightsizing: "usage",
  "downsize-risk": "usage",
  "rec-idle": "recommendations",
  "rec-commitment": "recommendations",
  "rec-modernize": "recommendations",
  "rec-schedule": "recommendations",
  "rec-network": "recommendations"
};
function commentGroupForCategory(category) {
  return COMMENT_GROUP_FOR_CATEGORY[category] ?? "waste";
}
var reviewCommentGroupsSchema = z20.object({
  cost: z20.boolean(),
  waste: z20.boolean(),
  usage: z20.boolean(),
  recommendations: z20.boolean()
});
var ALL_COMMENT_GROUPS_ON = {
  cost: true,
  waste: true,
  usage: true,
  recommendations: true
};
var reviewSettingsSchema = z20.object({
  enabled: z20.boolean(),
  /** Repositories (owner/repo or group/project) Lizrd reviews. Empty = every
   * connected IaC repository. */
  repositories: z20.array(z20.string()),
  /** Minimum severity a finding must reach to be surfaced / posted. */
  minSeverity: reviewSeveritySchema,
  /** Only flag a cost increase at or above this $/mo threshold (0 = flag any). */
  costThresholdMonthly: z20.number().nonnegative(),
  /** Which classes of comment Lizrd makes. Every group defaults on. */
  commentGroups: reviewCommentGroupsSchema
});
var reviewWriteAccessSchema = z20.object({
  /** Is an SCM (GitHub/GitLab) integration connected at all? */
  connected: z20.boolean(),
  /** The connected SCM provider, or null when none is connected. */
  provider: reviewProviderSchema.nullable(),
  /** Can Lizrd post comments + checks on PRs right now? */
  canPost: z20.boolean(),
  /** Short human reason when it can't post (e.g. "missing checks:write"). */
  detail: z20.string().nullable(),
  /** Where to send an admin to grant the missing permission (GitHub install
   * permissions page), or null when the fix is in-app / nothing is needed. */
  actionUrl: z20.string().nullable()
});
var reviewSettingsViewSchema = reviewSettingsSchema.extend({
  availableRepositories: z20.array(z20.string()),
  writeAccess: reviewWriteAccessSchema
});
var updateReviewSettingsSchema = reviewSettingsSchema.partial().refine((v) => Object.keys(v).length > 0, {
  message: "Provide at least one field to update."
});
var DEFAULT_REVIEW_SETTINGS = {
  enabled: false,
  repositories: [],
  minSeverity: "low",
  costThresholdMonthly: 0,
  commentGroups: ALL_COMMENT_GROUPS_ON
};
var reviewJobSchema = z20.object({
  organizationId: z20.string().uuid(),
  domainId: z20.string().uuid(),
  integrationId: z20.string().uuid(),
  provider: reviewProviderSchema,
  repoFullName: z20.string(),
  prNumber: z20.number().int(),
  headSha: z20.string(),
  baseSha: z20.string(),
  title: z20.string(),
  author: z20.string().nullable(),
  url: z20.string().nullable(),
  /** GitLab project id from the webhook (GitLab config carries no project id); null for GitHub. */
  projectId: z20.string().nullable(),
  /** Dev only: use a built-in fixture diff instead of fetching real PR files
   * (lets the full webhook→worker path be exercised offline on :8080). */
  devFixture: z20.boolean().optional()
});
var REVIEW_QUEUE = "review-pr";

// src/dashboard.ts
import { z as z21 } from "zod";
var assessmentEnvNoteSchema = z21.object({
  domain: z21.string(),
  reason: z21.string()
});
var orgAssessmentSchema = z21.object({
  /** ISO timestamp the assessment was generated. */
  generatedAt: z21.string(),
  /** The model that wrote it, or null for the deterministic fallback. */
  model: z21.string().nullable(),
  /** One punchy sentence — the headline state of the organization. */
  headline: z21.string(),
  /** A short paragraph (2–4 sentences) of narrative context. */
  summary: z21.string(),
  /** Domains doing well / on an up trend. */
  trending: z21.array(assessmentEnvNoteSchema),
  /** Domains that need attention. */
  attention: z21.array(assessmentEnvNoteSchema)
});
var activityCountsSchema = z21.object({
  produced: z21.number(),
  accepted: z21.number(),
  executed: z21.number(),
  validated: z21.number()
});
var backlogDaySchema = z21.object({
  day: z21.string(),
  open: z21.number(),
  inProgress: z21.number(),
  validated: z21.number()
});
var recommendationActivitySchema = z21.object({
  /** The window in days, or null for all time. */
  windowDays: z21.number().nullable(),
  total: activityCountsSchema,
  backlog: z21.array(backlogDaySchema),
  domains: z21.array(
    z21.object({
      domainId: z21.string(),
      name: z21.string(),
      total: activityCountsSchema
    })
  )
});

// src/leads.ts
import { z as z22 } from "zod";
var leadSourceSchema = z22.enum([
  "sandbox",
  "contact",
  "newsletter",
  "beta",
  "expert-review"
]);
var leadRoleSchema = z22.enum([
  "platform_devops_sre",
  "engineering_leadership",
  "finops",
  "founder_exec",
  "other"
]);
var leadSpendBandSchema = z22.enum([
  "under_50k",
  "50k_200k",
  "200k_1m",
  "over_1m",
  "unsure"
]);
var createLeadSchema = z22.object({
  email: z22.string().email().max(200),
  name: z22.string().trim().max(120).optional(),
  company: z22.string().trim().max(120).optional(),
  source: leadSourceSchema.default("sandbox"),
  role: leadRoleSchema.optional(),
  spendBand: leadSpendBandSchema.optional(),
  /** Honeypot; must be empty. Bots fill it, humans don't. */
  hp: z22.string().max(200).optional()
});
var leadStatusSchema = z22.enum(["NEW", "INVITED", "JOINED", "REJECTED"]);
var waitlistEntrySchema = z22.object({
  id: z22.string().uuid(),
  email: z22.string().email(),
  name: z22.string().nullable(),
  company: z22.string().nullable(),
  role: leadRoleSchema.nullable(),
  spendBand: leadSpendBandSchema.nullable(),
  status: leadStatusSchema,
  note: z22.string().nullable(),
  createdAt: z22.string(),
  invitedAt: z22.string().nullable()
});
var waitlistListResponseSchema = z22.object({
  entries: z22.array(waitlistEntrySchema),
  total: z22.number().int().nonnegative()
});
var waitlistDecisionSchema = z22.object({
  note: z22.string().trim().max(500).optional()
});
var validateBetaInviteSchema = z22.object({
  token: z22.string().min(1).max(200)
});
var betaInviteValidationSchema = z22.object({
  valid: z22.boolean(),
  email: z22.string().email().nullable()
});
var startSandboxSchema = z22.object({
  email: z22.string().email().max(200),
  name: z22.string().trim().max(120).optional(),
  company: z22.string().trim().max(120).optional(),
  turnstileToken: z22.string().min(1).max(4096)
});
var sandboxInviteReceiptSchema = z22.object({
  sent: z22.literal(true),
  email: z22.string().email()
});
var verifySandboxSchema = z22.object({
  token: z22.string().min(1).max(200)
});

// src/feedback.ts
import { z as z23 } from "zod";
var feedbackCategorySchema = z23.enum(["problem", "idea", "question", "general"]);
var feedbackAreaSchema = z23.enum([
  "dashboard",
  "recommendations",
  "domains",
  "integrations",
  "members",
  "account",
  "other"
]);
var feedbackSeveritySchema = z23.enum(["low", "medium", "high", "blocking"]);
var FEEDBACK_MESSAGE_MAX_WORDS = 500;
function countWords(text) {
  const trimmed = text.trim();
  return trimmed === "" ? 0 : trimmed.split(/\s+/).length;
}
var createFeedbackSchema = z23.object({
  category: feedbackCategorySchema,
  area: feedbackAreaSchema.default("other"),
  severity: feedbackSeveritySchema.nullable().default(null),
  subject: z23.string().trim().min(1, "Add a short subject").max(200),
  message: z23.string().trim().min(1, "Add some detail").max(5e3).refine(
    (v) => countWords(v) <= FEEDBACK_MESSAGE_MAX_WORDS,
    `Keep it under ${FEEDBACK_MESSAGE_MAX_WORDS} words`
  ),
  followUp: z23.boolean().default(true),
  context: z23.record(z23.unknown()).default({})
});
var feedbackReceiptSchema = z23.object({ id: z23.string(), reference: z23.string() });
var feedbackStatusSchema = z23.enum(["open", "accepted", "closed"]);

// src/admin.ts
import { z as z24 } from "zod";
var adminIdentitySchema = z24.object({
  id: z24.string().uuid(),
  email: z24.string().email(),
  name: z24.string(),
  /** True while the admin still owes mandatory MFA enrollment — the guard blocks
   * every data route until they enroll. */
  mfaPending: z24.boolean()
});
var adminSessionSchema = z24.object({
  accessToken: z24.string(),
  admin: adminIdentitySchema
});
var adminLoginSchema = z24.object({
  email: z24.string().email().max(200),
  password: z24.string().min(1).max(200)
});
var adminLoginResponseSchema = z24.discriminatedUnion("status", [
  z24.object({ status: z24.literal("mfa_required"), challengeToken: z24.string() }),
  z24.object({ status: z24.literal("mfa_setup"), session: adminSessionSchema })
]);
var adminMfaVerifySchema = z24.object({
  challengeToken: z24.string().min(1),
  code: z24.string().min(6).max(20)
});
var adminMfaEnrollResponseSchema = z24.object({
  secret: z24.string(),
  otpauthUri: z24.string(),
  qrDataUrl: z24.string()
});
var adminMfaActivateSchema = z24.object({
  code: z24.string().min(6).max(10)
});
var adminMfaActivateResponseSchema = z24.object({
  session: adminSessionSchema,
  recoveryCodes: z24.array(z24.string())
});
var kpiTrendSchema = z24.object({
  value: z24.number(),
  prev: z24.number(),
  deltaPct: z24.number().nullable()
});
var recCountsByStateSchema = z24.record(z24.string(), z24.number());
var adminSnapshotStatusSchema = z24.object({
  status: z24.enum(["NONE", "PENDING", "OK", "ERROR"]),
  computedAt: z24.string().nullable(),
  startedAt: z24.string().nullable(),
  ageMinutes: z24.number().nullable(),
  error: z24.string().nullable(),
  /** True while a PENDING run exists (drives the Recalculate button's spinner). */
  running: z24.boolean()
});
var adminOverviewSchema = z24.object({
  snapshot: adminSnapshotStatusSchema,
  totals: z24.object({
    organizations: z24.number(),
    users: z24.number(),
    domains: z24.number(),
    spendUnderManagement: z24.number()
  }),
  recommendations: z24.object({
    produced: kpiTrendSchema,
    accepted: kpiTrendSchema,
    rejected: kpiTrendSchema,
    validated: kpiTrendSchema,
    estSavings: kpiTrendSchema
  })
});
var adminTrafficLightSchema = z24.enum(["green", "yellow", "red", "none"]);
var adminOrgRowSchema = z24.object({
  organizationId: z24.string().uuid(),
  name: z24.string(),
  slug: z24.string(),
  kind: z24.string(),
  status: z24.string(),
  /** Live integration/billing traffic light (not snapshotted — reflects current status). */
  health: adminTrafficLightSchema,
  userCount: z24.number(),
  domainCount: z24.number(),
  integrationCount: z24.number(),
  spendUnderManagement: z24.number(),
  lastActivityAt: z24.string().nullable(),
  recCountsByState: recCountsByStateSchema,
  recProduced30d: z24.number(),
  recAccepted30d: z24.number(),
  recRejected30d: z24.number(),
  recValidated30d: z24.number(),
  estSavings30d: z24.number(),
  /** Privacy Mode flags (false = control restricted). `.nullish()` so the admin SPA
   * can deploy ahead of the API without blanking (admin contract rollout ordering). */
  ingestSourceEnabled: z24.boolean().nullish(),
  aiCorrelationEnabled: z24.boolean().nullish(),
  tokenizeResourceNames: z24.boolean().nullish()
});
var adminIntegrationSummarySchema = z24.object({
  provider: z24.string(),
  kind: z24.string(),
  status: z24.string()
});
var adminIntegrationRunSchema = z24.object({
  id: z24.string().uuid(),
  status: syncRunStatusSchema,
  trigger: syncTriggerSchema,
  error: z24.string().nullable(),
  errorCode: integrationErrorCodeSchema.nullable(),
  createdAt: z24.string(),
  finishedAt: z24.string().nullable()
});
var adminIntegrationRowSchema = z24.object({
  id: z24.string().uuid(),
  provider: z24.string(),
  kind: z24.string(),
  displayName: z24.string(),
  domainId: z24.string().uuid(),
  domainName: z24.string(),
  status: integrationStatusSchema,
  health: adminTrafficLightSchema,
  billingStatus: integrationBillingStatusSchema,
  lastSyncedAt: z24.string().nullable(),
  recentRuns: z24.array(adminIntegrationRunSchema),
  /** Error-first support fields. `.nullish()` so the admin SPA (which deploys ahead
   * of the API) tolerates an older API omitting them. `lastError` is scrubbed of
   * secrets at read time. */
  lastError: z24.string().nullish(),
  lastErrorCode: integrationErrorCodeSchema.nullish(),
  consecutiveFailures: z24.number().nullish(),
  syncCooldownUntil: z24.string().nullish()
});
var adminIntegrationListSchema = z24.array(adminIntegrationRowSchema);
var adminDomainRowSchema = z24.object({
  domainId: z24.string().uuid(),
  name: z24.string(),
  slug: z24.string(),
  integrations: z24.array(adminIntegrationSummarySchema),
  utilizationScore: z24.number().nullable(),
  cloudSpend: z24.number(),
  recCountsByState: recCountsByStateSchema
});
var adminUserRowSchema = z24.object({
  id: z24.string().uuid(),
  email: z24.string(),
  name: z24.string().nullable(),
  role: z24.string(),
  lastLoginAt: z24.string().nullable(),
  createdAt: z24.string()
});
var adminRejectedRecSchema = z24.object({
  id: z24.string().uuid(),
  title: z24.string(),
  organizationId: z24.string().uuid(),
  orgName: z24.string(),
  domainId: z24.string().uuid(),
  domainName: z24.string(),
  provider: z24.string(),
  resourceName: z24.string(),
  estimatedMonthlySaving: z24.number(),
  rejectionReason: z24.string().nullable(),
  rejectionNote: z24.string().nullable(),
  rejectedAt: z24.string().nullable()
});
function adminPage(item) {
  return z24.object({
    items: z24.array(item),
    total: z24.number(),
    page: z24.number(),
    pageSize: z24.number()
  });
}
var adminOrgListSchema = adminPage(adminOrgRowSchema);
var adminDomainListSchema = adminPage(adminDomainRowSchema);
var adminUserListSchema = adminPage(adminUserRowSchema);
var adminRejectedRecListSchema = adminPage(adminRejectedRecSchema);
var adminCostCategorySchema = z24.object({
  category: z24.string(),
  monthlyCost: z24.number()
});
var adminDomainDetailSchema = z24.object({
  domainId: z24.string().uuid(),
  name: z24.string(),
  slug: z24.string(),
  organizationId: z24.string().uuid(),
  orgName: z24.string(),
  /** Sum of modelled resource monthly cost (estimate). */
  estimatedMonthlyCost: z24.number(),
  /** Sum of the current month's OK billed ledger for the domain (null if none billed). */
  billedMonthlyCost: z24.number().nullish(),
  billingMonth: z24.string(),
  utilizationScore: z24.number().nullish(),
  resourceCount: z24.number(),
  recOpenCount: z24.number(),
  /** Sum of estimated monthly saving across non-terminal recommendations. */
  recoverableMonthly: z24.number(),
  recCountsByState: recCountsByStateSchema,
  costByCategory: z24.array(adminCostCategorySchema),
  integrations: z24.array(adminIntegrationRowSchema)
});
var adminResourceRowSchema = z24.object({
  id: z24.string().uuid(),
  type: z24.string(),
  name: z24.string(),
  provider: z24.string(),
  externalId: z24.string(),
  monthlyCost: z24.number(),
  costProvenance: z24.string().nullish(),
  gpu: z24.boolean(),
  metrics: z24.record(z24.unknown()).nullish(),
  stale: z24.boolean()
});
var adminResourceListSchema = adminPage(adminResourceRowSchema);
var adminRecRowSchema = z24.object({
  id: z24.string().uuid(),
  category: z24.string(),
  title: z24.string(),
  resourceName: z24.string(),
  nodeType: z24.string(),
  provider: z24.string(),
  estimatedMonthlySaving: z24.number(),
  savingConfidence: z24.string(),
  status: z24.string()
});
var adminRecListSchema = adminPage(adminRecRowSchema);
var adminRecEvidenceSchema = z24.object({ label: z24.string(), value: z24.string() });
var adminRecDetailSchema = z24.object({
  id: z24.string().uuid(),
  category: z24.string(),
  title: z24.string(),
  resourceName: z24.string(),
  nodeType: z24.string(),
  nodeExternalId: z24.string(),
  provider: z24.string(),
  rationale: z24.string(),
  suggestedAction: z24.string(),
  savingsBasis: z24.string(),
  confidenceReason: z24.string(),
  estimatedMonthlySaving: z24.number(),
  savingConfidence: z24.string(),
  severity: z24.string(),
  effortLevel: z24.string(),
  effortMinutes: z24.number(),
  removalIntent: z24.boolean(),
  evidence: z24.array(adminRecEvidenceSchema),
  code: z24.object({
    repo: z24.string().nullish(),
    file: z24.string().nullish(),
    resourceName: z24.string().nullish(),
    verifiedFromState: z24.boolean().nullish()
  }).nullish(),
  status: z24.string(),
  firstSeenAt: z24.string(),
  lastSeenAt: z24.string(),
  updatedAt: z24.string(),
  executedByName: z24.string().nullish(),
  executedAt: z24.string().nullish(),
  baselineMonthlyCost: z24.number().nullish(),
  realizedMonthlySaving: z24.number().nullish(),
  validatedAt: z24.string().nullish(),
  validationOutcome: z24.string().nullish(),
  validationAttribution: z24.string().nullish(),
  validationEvidence: z24.string().nullish(),
  // --- admin-only debug identity ---
  fingerprint: z24.string(),
  evidenceHash: z24.string(),
  priorityScore: z24.number(),
  priorityTier: z24.string(),
  userPriorityTier: z24.string().nullish(),
  hasGuidance: z24.boolean(),
  hasNarration: z24.boolean()
});
var adminIntegrationErrorRowSchema = z24.object({
  integrationId: z24.string().uuid(),
  provider: z24.string(),
  kind: z24.string(),
  displayName: z24.string(),
  organizationId: z24.string().uuid(),
  orgName: z24.string(),
  domainId: z24.string().uuid(),
  domainName: z24.string(),
  status: integrationStatusSchema,
  errorCode: integrationErrorCodeSchema.nullish(),
  lastError: z24.string().nullish(),
  billingStatus: integrationBillingStatusSchema,
  consecutiveFailures: z24.number(),
  syncCooldownUntil: z24.string().nullish(),
  lastSyncedAt: z24.string().nullish()
});
var adminIntegrationErrorListSchema = adminPage(adminIntegrationErrorRowSchema);
var adminSandboxActionSchema = z24.object({
  action: z24.string(),
  count: z24.number().int()
});
var adminLeadRowSchema = z24.object({
  id: z24.string().uuid(),
  email: z24.string(),
  name: z24.string().nullish(),
  company: z24.string().nullish(),
  role: z24.string().nullish(),
  spendBand: z24.string().nullish(),
  /** When the lead was first captured (the try-sandbox form submit). */
  requestedAt: z24.string(),
  /** True once they redeemed the link and a sandbox was provisioned. */
  activated: z24.boolean(),
  activatedAt: z24.string().nullish(),
  /** The activated sandbox org still exists (not yet reaped by TTL) — so live activity
   * below is available. False once the sandbox has expired and been cleaned up. */
  sandboxAlive: z24.boolean(),
  firstActionAt: z24.string().nullish(),
  lastActionAt: z24.string().nullish(),
  /** Minutes between first and last audited action (null if fewer than 2 actions). */
  activeMinutes: z24.number().nullish(),
  totalActions: z24.number().int(),
  /** Top audited action types (largest first) — the "roughly what they did" summary. */
  actions: z24.array(adminSandboxActionSchema)
});
var adminLeadListSchema = adminPage(adminLeadRowSchema);
var adminJourneyEventKindSchema = z24.enum([
  "sandbox_requested",
  "sandbox_activated",
  "view",
  "accept",
  "complete",
  "reject",
  "reopen",
  "comment",
  "ai_explanation",
  "feedback",
  "beta_requested",
  "beta_invited",
  "beta_joined"
]);
var adminJourneyEventSchema = z24.object({
  at: z24.string(),
  /** Kept as a string (not the enum) so an unknown future kind still parses; the UI
   * falls back to a generic marker. */
  kind: z24.string(),
  title: z24.string(),
  category: z24.string().nullish(),
  provider: z24.string().nullish(),
  estMonthlySaving: z24.number().nullish(),
  detail: z24.string().nullish()
});
var adminSandboxJourneySchema = z24.object({
  lead: z24.object({
    id: z24.string().uuid(),
    email: z24.string(),
    name: z24.string().nullish(),
    company: z24.string().nullish(),
    role: z24.string().nullish(),
    spendBand: z24.string().nullish(),
    requestedAt: z24.string(),
    ip: z24.string().nullish(),
    userAgent: z24.string().nullish(),
    /** Parsed from the user-agent for display (e.g. "iPhone", "Safari"). */
    device: z24.string().nullish(),
    browser: z24.string().nullish(),
    /** ISO-3166 alpha-2 country derived offline from the IP (coarse). */
    country: z24.string().nullish()
  }),
  /** The private-beta side of the funnel, if this person also applied. */
  beta: z24.object({
    applied: z24.boolean(),
    status: z24.string().nullish(),
    invitedAt: z24.string().nullish(),
    joinedAt: z24.string().nullish()
  }),
  sandbox: z24.object({
    activated: z24.boolean(),
    activatedAt: z24.string().nullish(),
    expiresAt: z24.string().nullish(),
    /** The sandbox org still exists (not yet TTL-reaped). When false the journey is
     * served from the stored snapshot. */
    sandboxAlive: z24.boolean(),
    organizationId: z24.string().nullish(),
    orgName: z24.string().nullish(),
    guestUserId: z24.string().nullish(),
    domains: z24.array(z24.string()),
    integrations: z24.array(z24.object({ provider: z24.string(), displayName: z24.string().nullish() }))
  }),
  session: z24.object({
    firstActivityAt: z24.string().nullish(),
    lastActivityAt: z24.string().nullish(),
    activeMinutes: z24.number().nullish()
  }),
  summary: z24.object({
    recommendationsViewed: z24.number().int(),
    recommendationsAccepted: z24.number().int(),
    recommendationsCompleted: z24.number().int(),
    recommendationsRejected: z24.number().int(),
    commentsPosted: z24.number().int(),
    aiExplanations: z24.number().int(),
    feedbackSubmitted: z24.number().int(),
    totalEvents: z24.number().int()
  }),
  /** A one-line highlight of the furthest the visitor got (e.g. completed the
   * accept→implement loop on "Downsize idle database" — $4,160/mo). Null if they
   * only browsed. */
  outcome: z24.string().nullish(),
  timeline: z24.array(adminJourneyEventSchema),
  /** When the durable snapshot behind this response was captured, and why. Null when
   * computed live from an alive sandbox. */
  capturedAt: z24.string().nullish(),
  capturedReason: z24.string().nullish(),
  /** False when the sandbox was reaped and no snapshot was ever captured — nothing
   * to show. The UI renders an empty/expired state. */
  dataAvailable: z24.boolean()
});
var adminRecomputeResponseSchema = z24.object({
  accepted: z24.boolean(),
  status: adminSnapshotStatusSchema
});
var ADMIN_SNAPSHOT_QUEUE = "admin-snapshot";
var adminSnapshotJobSchema = z24.object({
  snapshotId: z24.string().uuid().optional(),
  reason: z24.enum(["nightly", "manual"])
});
var adminMetricPointSchema = z24.object({ date: z24.string(), value: z24.number() });
var adminHealthPointSchema = z24.object({
  date: z24.string(),
  healthy: z24.number(),
  billingIssue: z24.number(),
  broken: z24.number()
});
var adminTopOrgSchema = z24.object({
  organizationId: z24.string(),
  organizationName: z24.string(),
  value: z24.number()
});
var adminHealthBreakdownSchema = z24.object({
  healthy: z24.number(),
  billingIssue: z24.number(),
  broken: z24.number()
});
var adminPlatformMetricsSchema = z24.object({
  days: z24.number(),
  syncs: z24.object({
    points: z24.array(adminMetricPointSchema),
    topOrgs: z24.array(adminTopOrgSchema)
  }),
  aiCalls: z24.object({
    points: z24.array(adminMetricPointSchema),
    topOrgs: z24.array(adminTopOrgSchema),
    capHitOrgs: z24.array(adminTopOrgSchema)
  }),
  health: z24.object({
    current: adminHealthBreakdownSchema,
    points: z24.array(adminHealthPointSchema)
  })
});
var adminFeedbackRowSchema = z24.object({
  id: z24.string().uuid(),
  createdAt: z24.string(),
  category: feedbackCategorySchema,
  severity: feedbackSeveritySchema.nullable(),
  subject: z24.string(),
  status: feedbackStatusSchema,
  organizationName: z24.string(),
  userEmail: z24.string()
});
var adminFeedbackListSchema = adminPage(adminFeedbackRowSchema);
var adminFeedbackDetailSchema = z24.object({
  id: z24.string().uuid(),
  reference: z24.string(),
  createdAt: z24.string(),
  status: feedbackStatusSchema,
  category: feedbackCategorySchema,
  area: feedbackAreaSchema.nullable(),
  severity: feedbackSeveritySchema.nullable(),
  subject: z24.string(),
  message: z24.string(),
  followUp: z24.boolean(),
  context: z24.record(z24.unknown()).nullable(),
  userId: z24.string().uuid(),
  userEmail: z24.string(),
  userName: z24.string().nullable(),
  role: z24.string(),
  organization: z24.object({
    id: z24.string().uuid(),
    name: z24.string(),
    kind: z24.string(),
    status: z24.string(),
    createdAt: z24.string()
  })
});
var setFeedbackStatusSchema = z24.object({ status: feedbackStatusSchema });
var adminCostSourceStatusSchema = z24.enum(["ok", "error", "not_configured"]);
var adminAwsCostSchema = z24.object({
  status: adminCostSourceStatusSchema,
  currency: z24.string(),
  /** Calendar label for the current month, e.g. "October 2026". */
  monthLabel: z24.string(),
  /** Unblended cost from the 1st of the month through today. */
  monthToDate: z24.number().nullable(),
  /** Full-month projection (Cost Explorer forecast, UNBLENDED_COST). */
  projectedMonth: z24.number().nullable(),
  /** Same calendar window (1st → same day) of the previous month. */
  lastMonthToDate: z24.number().nullable(),
  /** Previous month's complete total. */
  lastMonthTotal: z24.number().nullable(),
  error: z24.string().nullable()
});
var adminAnthropicCostSchema = z24.object({
  status: adminCostSourceStatusSchema,
  currency: z24.string(),
  monthLabel: z24.string(),
  /** Month-to-date Anthropic API spend for the org, in USD. */
  monthToDate: z24.number().nullable(),
  error: z24.string().nullable()
});
var adminCostMonitoringSchema = z24.object({
  generatedAt: z24.string(),
  aws: adminAwsCostSchema,
  anthropic: adminAnthropicCostSchema
});

// src/blog.ts
import { z as z25 } from "zod";
var blogPostStatusSchema = z25.enum(["DRAFT", "PUBLISHED"]);
var blogPostSchema = z25.object({
  id: z25.string().uuid(),
  slug: z25.string(),
  title: z25.string(),
  description: z25.string(),
  /** Markdown source. */
  body: z25.string(),
  /** Sanitized HTML rendered from `body`. */
  bodyHtml: z25.string(),
  status: blogPostStatusSchema,
  labels: z25.array(z25.string()),
  author: z25.string(),
  publishedAt: z25.string().nullable(),
  createdAt: z25.string(),
  updatedAt: z25.string()
});
var blogPostListItemSchema = z25.object({
  id: z25.string().uuid(),
  slug: z25.string(),
  title: z25.string(),
  description: z25.string(),
  status: blogPostStatusSchema,
  labels: z25.array(z25.string()),
  author: z25.string(),
  publishedAt: z25.string().nullable(),
  updatedAt: z25.string()
});
var blogSlugSchema = z25.string().trim().min(1).max(200).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase words separated by single hyphens.");
var blogPostInputSchema = z25.object({
  title: z25.string().trim().min(1).max(200),
  slug: blogSlugSchema,
  description: z25.string().trim().max(160),
  body: z25.string().max(1e5),
  labels: z25.array(z25.string().trim().min(1).max(40)).max(20).default([]),
  author: z25.string().trim().min(1).max(120).optional()
});
var blogPostListSchema = z25.object({
  items: z25.array(blogPostListItemSchema),
  total: z25.number(),
  deploy: z25.lazy(() => blogDeployStatusSchema)
});
var blogPreviewInputSchema = z25.object({ body: z25.string().max(1e5) });
var blogPreviewSchema = z25.object({ html: z25.string() });
var blogDeployStatusSchema = z25.object({
  contentRevision: z25.number(),
  deployedRevision: z25.number(),
  changesBehind: z25.number(),
  lastDeployRequestedAt: z25.string().nullable(),
  deployConfigured: z25.boolean()
});
var blogDeployResultSchema = z25.object({
  dispatched: z25.boolean(),
  message: z25.string(),
  status: blogDeployStatusSchema
});
var publicBlogPostSchema = z25.object({
  slug: z25.string(),
  title: z25.string(),
  description: z25.string(),
  labels: z25.array(z25.string()),
  author: z25.string(),
  publishedAt: z25.string(),
  updatedAt: z25.string(),
  /** Sanitized, rendered HTML for the post body. */
  html: z25.string(),
  readingMinutes: z25.number()
});
var publicBlogListSchema = z25.object({ posts: z25.array(publicBlogPostSchema) });
export {
  ADMIN_SNAPSHOT_QUEUE,
  ALL_COMMENT_GROUPS_ON,
  BILLING_ACCESS_ARM_DEBOUNCE_MS,
  BILLING_ACCESS_CLEAR_DEBOUNCE_MS,
  BILLING_GRACE_DAYS,
  COMMENT_GROUP_FOR_CATEGORY,
  COMMON_TIMEZONES,
  COMPLIANCE_EVIDENCE_DISCLAIMER,
  COMPLIANCE_FRAMEWORK_REFERENCE,
  DATA_REGIONS,
  DEFAULT_NOTIFICATION_PREFERENCES,
  DEFAULT_REVIEW_SETTINGS,
  FEATURE_FLAGS,
  FEATURE_FLAG_KEYS,
  FEEDBACK_MESSAGE_MAX_WORDS,
  GRAPH_SYNC_QUEUE,
  INTEGRATION_SYNC_QUEUE,
  NIS2_CONTROLS,
  NIS2_CONTROL_BY_ID,
  ONBOARDING_STEPS,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULES,
  PRICING_BANDS,
  Permission,
  REGION_PRESENTATION,
  REJECTION_REASON_LABELS,
  REVIEW_COMMENT_GROUPS,
  REVIEW_COMMENT_GROUP_CATALOG,
  REVIEW_QUEUE,
  ROLE_PERMISSIONS,
  TRIAL_DAYS,
  TYPE_CATEGORY,
  acceptInvitationSchema,
  accountSpendSchema,
  activityCountsSchema,
  adminAnthropicCostSchema,
  adminAwsCostSchema,
  adminCostCategorySchema,
  adminCostMonitoringSchema,
  adminCostSourceStatusSchema,
  adminDomainDetailSchema,
  adminDomainListSchema,
  adminDomainRowSchema,
  adminFeatureFlagListSchema,
  adminFeatureFlagSchema,
  adminFeedbackDetailSchema,
  adminFeedbackListSchema,
  adminFeedbackRowSchema,
  adminHealthBreakdownSchema,
  adminHealthPointSchema,
  adminIdentitySchema,
  adminIntegrationErrorListSchema,
  adminIntegrationErrorRowSchema,
  adminIntegrationListSchema,
  adminIntegrationRowSchema,
  adminIntegrationRunSchema,
  adminIntegrationSummarySchema,
  adminJourneyEventKindSchema,
  adminJourneyEventSchema,
  adminLeadListSchema,
  adminLeadRowSchema,
  adminLoginResponseSchema,
  adminLoginSchema,
  adminMetricPointSchema,
  adminMfaActivateResponseSchema,
  adminMfaActivateSchema,
  adminMfaEnrollResponseSchema,
  adminMfaVerifySchema,
  adminOrgListSchema,
  adminOrgRowSchema,
  adminOverviewSchema,
  adminPage,
  adminPlatformMetricsSchema,
  adminRecDetailSchema,
  adminRecEvidenceSchema,
  adminRecListSchema,
  adminRecRowSchema,
  adminRecomputeResponseSchema,
  adminRejectedRecListSchema,
  adminRejectedRecSchema,
  adminResourceListSchema,
  adminResourceRowSchema,
  adminSandboxActionSchema,
  adminSandboxJourneySchema,
  adminSessionSchema,
  adminSnapshotJobSchema,
  adminSnapshotStatusSchema,
  adminTopOrgSchema,
  adminTrafficLightSchema,
  adminUserListSchema,
  adminUserRowSchema,
  advanceOnboardingSchema,
  assessmentEnvNoteSchema,
  auditEventSchema,
  authResponseSchema,
  awsConfigSchema,
  awsConnectRequestSchema,
  awsKubernetesAccessTestResultSchema,
  awsPrepareResultSchema,
  awsStateBackendSchema,
  awsStateBackendTestResultSchema,
  awsTestStateBackendRequestSchema,
  azureConfigSchema,
  azureConnectRequestSchema,
  azurePrepareResultSchema,
  azureStateBackendSchema,
  azureTestStateBackendRequestSchema,
  backlogDaySchema,
  bandForSpend,
  bandSchema,
  betaInviteValidationSchema,
  billingAccessStateFrom,
  billingAccessStateSchema,
  billingCostStatusSchema,
  billingHealthTransition,
  billingStatementSchema,
  billingSummarySchema,
  blogDeployResultSchema,
  blogDeployStatusSchema,
  blogPostInputSchema,
  blogPostListItemSchema,
  blogPostListSchema,
  blogPostSchema,
  blogPostStatusSchema,
  blogPreviewInputSchema,
  blogPreviewSchema,
  blogSlugSchema,
  bulkRecommendationOpSchema,
  bulkRecommendationResultSchema,
  bulkRecommendationSchema,
  cachedExplanationSchema,
  canAssignRole,
  capabilityFileSchema,
  capabilityMapEntrySchema,
  categoryOf,
  commentGroupForCategory,
  companySizeSchema,
  complianceControlIdSchema,
  complianceDriftSchema,
  complianceEvidencePackSchema,
  complianceFrameworkSchema,
  complianceGapSchema,
  complianceGuardrailFlagSchema,
  compliancePostureSchema,
  complianceSettingsSchema,
  complianceSnapshotSchema,
  complianceSummarySchema,
  complianceTrendSchema,
  connectIntegrationSchema,
  controlGateSchema,
  controlPostureSchema,
  controlStatusSchema,
  costAttributionSchema,
  costBreakdownCategorySchema,
  costBreakdownProviderSchema,
  costBreakdownResourceSchema,
  costBreakdownSchema,
  costOf,
  costProvenanceOf,
  costSourceSchema,
  costTrendsSchema,
  countWords,
  createDomainSchema,
  createFeedbackSchema,
  createInvitationSchema,
  createLeadSchema,
  createRecommendationCommentSchema,
  dataRegionSchema,
  defaultControlGate,
  deleteAccountSchema,
  dependencyStatusSchema,
  domainAssessmentSchema,
  domainComplianceSchema,
  domainHealthSchema,
  domainHealthStatusSchema,
  domainHistorySchema,
  domainSavingsSchema,
  domainSchema,
  domainStatusSchema,
  domainSyncResultSchema,
  domainSyncStatusSchema,
  enableNameTokenizationResultSchema,
  enableNameTokenizationSchema,
  enabledFeaturesSchema,
  estimateConfidenceLevelSchema,
  estimateConfidenceSchema,
  evidenceAssetControlSchema,
  evidenceAssetSchema,
  evidencePolicyLineSchema,
  featureCohortMemberSchema,
  featureCohortSchema,
  featureFlagKeySchema,
  featureFlagOverrideSchema,
  featureFlagStateSchema,
  feedbackAreaSchema,
  feedbackCategorySchema,
  feedbackReceiptSchema,
  feedbackSeveritySchema,
  feedbackStatusSchema,
  forgotPasswordSchema,
  gapSeveritySchema,
  gcpConfigSchema,
  gcpConnectRequestSchema,
  gcpPrepareResultSchema,
  gcpStateBackendSchema,
  gcpTestStateBackendRequestSchema,
  githubConfigSchema,
  githubConnectRequestSchema,
  githubConnectResultSchema,
  gitlabConfigSchema,
  gitlabConnectRequestSchema,
  gitlabConnectResultSchema,
  grantDomainAccessSchema,
  graphNeighborsSchema,
  graphPathSchema,
  graphSearchResultSchema,
  graphSyncJobSchema,
  graphSyncStageSchema,
  guideRecommendationSchema,
  healthStatusSchema,
  industrySchema,
  integrationBillingStatusSchema,
  integrationErrorCodeSchema,
  integrationHealthRowSchema,
  integrationHealthSchema,
  integrationKindSchema,
  integrationProviderSchema,
  integrationSchema,
  integrationStatusSchema,
  integrationSyncJobSchema,
  integrationSyncStatusSchema,
  integrationValidationResultSchema,
  intendedSignupRegion,
  invitationPreviewSchema,
  invitationSchema,
  invitationStatusSchema,
  jobTitleSchema,
  knowledgeEdgeSchema,
  knowledgeGraphSchema,
  knowledgeGraphStatsSchema,
  knowledgeNodeDetailSchema,
  knowledgeNodeSchema,
  knowledgeRelationshipTypeSchema,
  kpiTrendSchema,
  kubernetesAccessSchema,
  leadRoleSchema,
  leadSourceSchema,
  leadSpendBandSchema,
  leadStatusSchema,
  livenessSchema,
  loginResultSchema,
  loginSchema,
  memberSchema,
  metricKeySchema,
  metricPointSchema,
  mfaActivateSchema,
  mfaChallengeSchema,
  mfaEnrollStartSchema,
  mfaFactorSchema,
  mfaFactorTypeSchema,
  mfaPolicySchema,
  mfaRecoveryCodesSchema,
  mfaStatusSchema,
  mfaVerifyLoginSchema,
  monthProgress,
  monthlyCharge,
  nameTokenizationResultSchema,
  normalizeNotificationPreferences,
  normalizedEntitySchema,
  notificationListSchema,
  notificationPreferencesSchema,
  notificationSchema,
  observationSourceSchema,
  onboardingStateSchema,
  onboardingStepSchema,
  orgAssessmentSchema,
  orgCompliancePostureSchema,
  orgKindSchema,
  orgRoleSchema,
  orgSavingsSummarySchema,
  organizationSchema,
  organizationStatusSchema,
  ownerCostSchema,
  passwordSchema,
  permissionsForRole,
  pricingBands,
  privacyPurgeResultSchema,
  projectMonthlySpend,
  projectionBasisSchema,
  providerCostTrendSchema,
  publicBlogListSchema,
  publicBlogPostSchema,
  recCountsByStateSchema,
  recommendationActivitySchema,
  recommendationCategorySchema,
  recommendationCodeSchema,
  recommendationCommentSchema,
  recommendationCommentsSchema,
  recommendationDirection,
  recommendationEffortLevelSchema,
  recommendationEventSchema,
  recommendationEvidenceSchema,
  recommendationGuidanceContentSchema,
  recommendationGuidanceSchema,
  recommendationHistorySchema,
  recommendationMetricsSchema,
  recommendationNarrationSchema,
  recommendationPriorityTierSchema,
  recommendationRiskLevelSchema,
  recommendationSchema,
  recommendationSeveritySchema,
  recommendationStatusSchema,
  recommendationsSchema,
  regionForCountry,
  regionScanSchema,
  registerSchema,
  registrationInfoSchema,
  rejectionReasonSchema,
  resetPasswordSchema,
  resourceExplanationSchema,
  resourceHistorySchema,
  reviewCommentGroupSchema,
  reviewCommentGroupsSchema,
  reviewCommentSchema,
  reviewFindingKindSchema,
  reviewJobSchema,
  reviewListSchema,
  reviewProviderSchema,
  reviewRepoGroupSchema,
  reviewRiskLevelSchema,
  reviewSchema,
  reviewSettingsSchema,
  reviewSettingsViewSchema,
  reviewSeveritySchema,
  reviewStatusSchema,
  reviewWriteAccessSchema,
  roleHasPermissions,
  sandboxInviteReceiptSchema,
  savingsReconciliationSchema,
  savingsReconciliationStatusSchema,
  scoreTrendPointSchema,
  setAwsKubernetesAccessSchema,
  setAwsStateBackendSchema,
  setDomainStatusSchema,
  setFeatureOverrideSchema,
  setFeatureRolloutSchema,
  setFeatureStateSchema,
  setFeedbackStatusSchema,
  setMemberDomainsSchema,
  setRecommendationAssigneeSchema,
  setRecommendationPrioritySchema,
  setStateBackendSchema,
  snapshotControlSchema,
  snapshotGapSchema,
  spendUnderManagementSchema,
  startSandboxSchema,
  stateBackendSchema,
  stateBackendTestResultSchema,
  statementStatusSchema,
  subscriptionPlanSchema,
  subscriptionUsageSchema,
  syncRunSchema,
  syncRunStatusSchema,
  syncTriggerSchema,
  testAwsStateBackendSchema,
  testStateBackendSchema,
  typeCostTrendSchema,
  unattributedResourceSchema,
  unbilledCloudSchema,
  updateComplianceSettingsSchema,
  updateDomainSchema,
  updateMemberRoleSchema,
  updateMfaPolicySchema,
  updateOrganizationSchema,
  updatePrivacySettingsResultSchema,
  updatePrivacySettingsSchema,
  updateProfileSchema,
  updateRecommendationSchema,
  updateReviewSettingsSchema,
  usageMeterSchema,
  userProfileSchema,
  validateBetaInviteSchema,
  validationAttributionSchema,
  validationOutcomeSchema,
  verifyEmailSchema,
  verifySandboxSchema,
  versionInfoSchema,
  waitlistDecisionSchema,
  waitlistEntrySchema,
  waitlistListResponseSchema,
  webauthnLoginOptionsSchema,
  webauthnLoginVerifySchema,
  webauthnOptionsSchema,
  webauthnRegisterVerifySchema
};
//# sourceMappingURL=index.mjs.map